<template>
  <div class="container-fluid p-4 shadow-sm rounded" :class="themeClass">

    <!-- Avisos del superadmin (descartables, no bloquean la facturacion) -->
    <div
      v-for="n in notificacionesActivas"
      :key="n.id"
      class="d-flex justify-content-between align-items-start py-2 px-3 mb-2 aviso-sistema"
    >
      <span>
        <i :class="iconoAvisoSistema(n.tipo)" class="me-2 aviso-sistema__icono"></i>
        <strong class="aviso-sistema__tipo">{{ etiquetaTipo(n.tipo) }}:</strong>
        {{ n.mensaje }}
      </span>
      <button type="button" class="btn-close" aria-label="Cerrar" @click="descartarNotificacion(n.id)"></button>
    </div>

    <!-- Aviso de solo lectura (factura cargada desde Documentos Emitidos, ya no editable) -->
    <div v-if="soloLectura" class="alert alert-warning d-flex justify-content-between align-items-center py-2 mb-3">
      <span>
        <i class="bi bi-eye-fill me-2"></i>
        Estás viendo esta factura en <strong>modo solo lectura</strong>. No se puede modificar porque ya fue firmada, enviada o autorizada por el SRI.
      </span>
      <router-link to="/ventas/documentos-emitidos" class="btn btn-outline-secondary btn-sm">
        <i class="bi bi-arrow-left me-1"></i>Volver
      </router-link>
    </div>

    <fieldset :disabled="soloLectura" :class="{ 'campos-solo-lectura': soloLectura }" class="borde-fieldset-reset">

    <!-- ENCABEZADO DE LA FACTURA -->
    <div class="card mb-4">
      <div class="card-header bg-primary text-white fw-bold py-1">
        Encabezado de Factura
      </div>
      <div class="card-body py-2">
        <div class="row gy-2">
          <div class="col-md-3">
            <label class="form-label small mb-1">Fecha</label>
            <input
              type="date"
              class="form-control form-control-sm"
              v-model="invoiceHeader.fecha"
              disabled
            />
          </div>
          <div class="col-md-3">
            <label class="form-label small mb-1">Punto Emisión</label>
            <select
              class="form-select form-select-sm"
              v-model="invoiceHeader.puntoEmision"
              :disabled="loadingEmissionPoints"
            >
              <option v-if="loadingEmissionPoints" disabled value="">Cargando...</option>
              <option v-else-if="emissionPoints.length === 0" disabled value="">Sin puntos disponibles</option>
              <option
                v-for="point in emissionPoints"
                :key="point.id"
                :value="point.emission_point_code"
              >
                {{ point.full_code }}
              </option>
            </select>
          </div>
          <div class="col-md-3">
            <label class="form-label small mb-1">Secuencial</label>
            <input
              type="text"
              class="form-control form-control-sm"
              v-model="invoiceHeader.secuencial"
              disabled
            />
          </div>
          <div class="col-md-3">
            <label class="form-label small mb-1">Ambiente</label>
            <input
              type="text"
              class="form-control form-control-sm"
              v-model="invoiceHeader.ambiente"
              disabled
            />
          </div>
          <div class="col-md-4">
            <label class="form-label small mb-1">Vendedor</label>
            <input
              type="text"
              class="form-control form-control-sm"
              v-model="invoiceHeader.vendedor"
              disabled
            />
            <small class="text-muted d-block mt-1">
              <a href="#" class="text-decoration-underline" @click.prevent="cerrarSesionSiNoEresTu">¿No eres tú? Cierra sesión</a>
            </small>
          </div>
          <div class="col-md-4">
            <label class="form-label small mb-1">Bodega</label>
            <select
              class="form-select form-select-sm"
              v-model="invoiceHeader.bodega"
              :disabled="loadingAlmacenes"
            >
              <option v-if="loadingAlmacenes" disabled value="">Cargando...</option>
              <option v-else-if="almacenes.length === 0" disabled value="">Sin almacenes</option>
              <option
                v-for="almacen in almacenes"
                :key="almacen.id"
                :value="almacen.nombre"
              >
                {{ almacen.nombre }}
              </option>
            </select>
          </div>
          <!-- Tipo de factura: Contado / Crédito interno -->
          <div class="col-md-4 d-flex flex-column justify-content-end">
            <label class="form-label small mb-1">Tipo de Factura</label>
            <div class="form-check form-switch mb-0">
              <input
                class="form-check-input"
                type="checkbox"
                id="esCreditoInterno"
                v-model="invoiceHeader.esCreditoInterno"
                role="switch"
              />
              <label class="form-check-label small" for="esCreditoInterno">
                <span :class="invoiceHeader.esCreditoInterno ? 'text-warning fw-semibold' : ''">
                  {{ invoiceHeader.esCreditoInterno ? 'A crédito' : 'Contado' }}
                </span>
              </label>
            </div>
            <input
              v-if="invoiceHeader.esCreditoInterno"
              type="number"
              class="form-control form-control-sm mt-1"
              placeholder="Días de crédito"
              v-model.number="invoiceHeader.diasCredito"
              min="1"
              max="365"
            />
          </div>
        </div>
      </div>
    </div>

    <!-- ALERTA CAJA NO ABIERTA -->
    <div v-if="cajaActiva === null && !isPlanBasico" class="alert alert-danger d-flex align-items-center gap-2 mb-3" role="alert">
      <i class="bi bi-lock-fill fs-5"></i>
      <div class="flex-grow-1">
        <strong>No hay caja abierta.</strong>
        Debe abrir una caja en el módulo de <strong>Control de Caja</strong> antes de poder emitir facturas.
      </div>
    </div>

    <!-- ALERTA DEUDA CLIENTE -->
    <div v-if="alertaDeuda.mostrar" class="alert alert-warning alert-dismissible d-flex align-items-center gap-2 mb-3" role="alert">
      <i class="bi bi-exclamation-triangle-fill"></i>
      <div class="flex-grow-1">
        <strong>Este cliente tiene deuda pendiente.</strong>
        {{ alertaDeuda.cantidad }} factura(s) a crédito por un total de
        <strong>$ {{ alertaDeuda.montoTotal.toFixed(2) }}</strong> pendiente(s) de pago.
      </div>
      <button type="button" class="btn-close" @click="alertaDeuda.mostrar = false"></button>
    </div>

    <!-- DATOS DEL COMPRADOR -->
    <div class="card mb-4">
      <div class="card-header bg-primary text-white fw-bold py-1">
        Datos del Comprador
      </div>
      <div class="card-body py-2">
        <div class="row gy-2">
          <!-- Fila 1: Identificación y Tipo -->
          <div class="col-md-3">
            <label class="form-label small mb-1">Tipo Identificación</label>
            <select
              class="form-select form-select-sm"
              v-model="buyer.tipoIdentificacion"
              @change="onCambioTipoIdentificacion"
            >
              <option value="">Seleccione</option>
              <option v-for="type in sriTypes" :key="type.code" :value="type.code">
                {{ type.name }}
              </option>
            </select>
          </div>
          <div class="col-md-3">
            <label class="form-label small mb-1">Identificación</label>
            <div class="input-group input-group-sm">
              <input
                type="text"
                class="form-control form-control-sm"
                placeholder="RUC / Cédula / Pasaporte"
                v-model="buyer.identificacion"
                @input="marcarClienteModificado"
                @blur="buscarClienteAutomatico('identificacion')"
                @keyup.enter="buscarClienteAutomatico('identificacion')"
              />
              <span v-if="buscandoCliente" class="input-group-text">
                <span class="spinner-border spinner-border-sm" role="status"></span>
              </span>
              <button
                v-if="buyer.clientId"
                type="button"
                class="btn btn-outline-primary btn-sm"
                @click="modalEditarClienteVisible = true"
                title="Editar datos del cliente"
              >
                <i class="bi bi-pencil-fill"></i>
              </button>
              <button 
                type="button" 
                class="btn btn-outline-success btn-sm" 
                @click="abrirModalNuevoCliente"
                title="Crear nuevo cliente"
              >
                <i class="bi bi-plus-lg"></i>
              </button>
            </div>
          </div>
          <div class="col-md-6">
            <label class="form-label small mb-1">Nombres / Razón Social</label>
            <div class="position-relative">
              <div class="input-group input-group-sm">
                <input
                  type="text"
                  class="form-control form-control-sm"
                  placeholder="Ingrese nombre o razón social"
                  v-model="buyer.razonSocial"
                  @input="alCambiarNombreComprador"
                  @blur="cerrarSugerenciasConDelay"
                  @keydown.down.prevent="navegarSugerencia(1)"
                  @keydown.up.prevent="navegarSugerencia(-1)"
                  @keydown.enter.prevent="seleccionarSugerenciaActiva"
                  @keydown.escape="cerrarSugerencias"
                  autocomplete="off"
                />
                <span v-if="buscandoCliente" class="input-group-text">
                  <span class="spinner-border spinner-border-sm" role="status"></span>
                </span>
              </div>
              <!-- Lista de sugerencias -->
              <ul 
                v-if="sugerenciasClientes.length > 0 && mostrarSugerencias" 
                class="list-group position-absolute w-100 shadow-sm sugerencias-lista"
              >
                <li 
                  v-for="(cliente, index) in sugerenciasClientes" 
                  :key="cliente.id"
                  class="list-group-item list-group-item-action py-1 px-2 small"
                  :class="{ 'active': index === indiceSugerenciaActiva }"
                  @mousedown.prevent="seleccionarCliente(cliente)"
                >
                  <div class="fw-semibold">{{ cliente.business_name }}</div>
                  <small class="text-muted">{{ cliente.identification_number }}</small>
                </li>
              </ul>
            </div>
          </div>
          <!-- Fila 2: Contacto -->
          <div class="col-md-4">
            <label class="form-label small mb-1">Correo Electrónico</label>
            <input
              type="email"
              class="form-control form-control-sm"
              placeholder="correo@ejemplo.com"
              v-model="buyer.correo"
              @input="marcarClienteModificado"
            />
          </div>
          <div class="col-md-2">
            <label class="form-label small mb-1">Teléfono</label>
            <input
              type="text"
              class="form-control form-control-sm"
              placeholder="Teléfono"
              v-model="buyer.telefono"
              @input="marcarClienteModificado"
            />
          </div>
          <div class="col-md-2">
            <label class="form-label small mb-1">Celular</label>
            <input
              type="text"
              class="form-control form-control-sm"
              placeholder="Celular"
              v-model="buyer.celular"
            />
          </div>
          <div class="col-md-4">
            <label class="form-label small mb-1">Dirección</label>
            <input
              type="text"
              class="form-control form-control-sm"
              placeholder="Dirección del cliente"
              v-model="buyer.direccion"
              @input="marcarClienteModificado"
            />
          </div>
        </div>
      </div>
    </div>

    <!-- PRODUCTOS Y SERVICIOS -->
    <div class="card mb-4">
      <div class="card-header bg-primary text-white fw-bold py-1">
        Productos y Servicios
      </div>
      <div class="card-body py-2">
        <!-- Buscador de productos -->
        <div class="row gy-2 mb-3">
          <div class="col-12">
            <label class="form-label small mb-1">Buscar Producto</label>
            <input
              ref="inputBusquedaProducto"
              type="text"
              class="form-control form-control-sm"
              placeholder="Escribe código, nombre o código de barras..."
              v-model="productSearchQuery"
              @input="onProductSearchInput"
              @keyup.enter.prevent="buscarProducto"
              :class="{ 'is-invalid': productSearchError }"
            />
            <div v-if="loadingProductSearch" class="mt-1">
              <div class="spinner-border spinner-border-sm" role="status">
                <span class="visually-hidden">Cargando...</span>
              </div>
            </div>
            <ul v-if="productSuggestions.length" class="list-group mt-1 border" :class="themeClass" style="max-height: 200px; overflow-y: auto; background-color: var(--bg-color); border-color: var(--border-color);">
              <li v-for="p in productSuggestions" :key="p.id" @click="selectProduct(p)" class="list-group-item list-group-item-action small border-0" :class="themeClass" style="color: var(--text-color); background-color: var(--bg-color); border-bottom: 1px solid var(--border-color); cursor: pointer; transition: background-color 0.2s ease;" onmouseover="this.style.backgroundColor='var(--hover-bg)'" onmouseout="this.style.backgroundColor='var(--bg-color)'">
                <strong>{{ p.codigo_principal }}</strong> - {{ p.nombre_producto }}
              </li>
            </ul>
            <div v-if="productSearchError" class="invalid-feedback d-block">
              {{ productSearchError }}
            </div>
          </div>
        </div>

        <div v-if="modoDescuento === 'aprobacion' && productsInInvoice.some(p => Number(p.descuento) > 0)" class="alert alert-warning py-2 mb-2">
          Esta factura tiene descuento aplicado: quedará pendiente de aprobación del administrador y no podrá firmarse/enviarse hasta que se apruebe.
        </div>

        <div class="table-responsive">
          <table class="table table-bordered table-sm align-middle">
            <thead class="table-light text-center small">
              <tr>
                <th>#</th>
                <th>CÓDIGO</th>
                <th>NOMBRE</th>
                <th>CANTIDAD</th>
                <th>PRECIO</th>
                <th>%DESC</th>
                <th>SUBTOTAL</th>
                <th>% IVA</th>
                <th>IVA</th>
                <th>TOTAL</th>
                <th></th>
              </tr>
            </thead>
            <tbody>
              <tr v-if="productsInInvoice.length === 0">
                <td colspan="11" class="text-center text-muted">
                  Sin productos agregados
                </td>
              </tr>
              <tr
                v-for="(product, index) in productsInInvoice"
                :key="product.id || index"
              >
                <td>{{ index + 1 }}</td>
                <td>{{ product.codigo }}</td>
                <td>
                  <input
                    v-if="product.permiteNombreManual"
                    type="text"
                    class="form-control form-control-sm"
                    v-model="product.nombre"
                  />
                  <span v-else>{{ product.nombre }}</span>
                  <div
                    class="stock-inline-linea"
                    :class="product.tipoProducto === 'servicio' || product.cantidad <= product.stockDisponible ? 'stock-inline-linea--ok' : 'stock-inline-linea--warn'"
                  >
                    {{ product.tipoProducto === 'servicio' ? 'Servicio' : 'Disponible: ' + product.stockDisponible.toFixed(0) }}
                  </div>
                </td>
                <td>
                  <input
                    type="number"
                    class="form-control form-control-sm text-center"
                    v-model.number="product.cantidad"
                    min="1"
                    @change="calcularLineaProducto(product)"
                  />
                </td>
                <td>
                  <div class="d-flex align-items-center gap-1">
                    <select
                      class="form-select form-select-sm"
                      style="min-width: 110px; font-size: 0.8rem;"
                      v-model="product.precioSeleccionado"
                      @change="cambiarPrecioProducto(product)"
                    >
                      <option
                        v-for="precio in product.preciosDisponibles"
                        :key="precio.tipo_precio"
                        :value="precio.tipo_precio"
                      >
                        {{ precio.tipo_precio }} — ${{ parseFloat(precio.precio).toFixed(2) }}
                      </option>
                      <option v-if="product.permitePrecioManual" value="__manual__">Precio manual</option>
                    </select>
                    <input
                      v-if="product.precioSeleccionado === '__manual__'"
                      type="number"
                      class="form-control form-control-sm text-end"
                      min="0"
                      step="0.0001"
                      v-model.number="product.precioUnitario"
                      @input="calcularLineaProducto(product)"
                    />
                  </div>
                </td>
                <td>
                  <input
                    type="number"
                    class="form-control form-control-sm text-center"
                    v-model.number="product.descuento"
                    min="0"
                    max="100"
                    :disabled="modoDescuento === 'bloqueado'"
                    :title="modoDescuento === 'bloqueado' ? 'Su rol no tiene permiso para aplicar descuentos' : ''"
                    @change="calcularLineaProducto(product)"
                  />
                </td>
                <td>{{ product.subtotalSinImpuestos.toFixed(2) }}</td>
                <td class="text-center">
                  <span v-if="product.porcentajeIVA > 0" class="badge bg-warning text-dark">{{ product.porcentajeIVA }}%</span>
                  <span v-else class="badge bg-secondary">0%</span>
                </td>
                <td>{{ product.iva.toFixed(2) }}</td>
                <td>{{ product.total.toFixed(2) }}</td>
                <td class="text-center">
                  <button
                    class="btn btn-danger btn-sm"
                    @click="eliminarProducto(index)"
                  >
                    <i class="bi bi-trash"></i>
                  </button>
                </td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>
    </div>

    <!-- FORMA DE PAGO Y TOTALES -->
    <div class="row mb-4 gy-3">
      <!-- Columna izquierda -->
      <div class="col-md-6 d-flex flex-column gap-3">
        <!-- DESCRIPCIÓN -->
        <div class="card">
          <div class="card-header bg-primary text-white fw-bold py-1">
            Descripción
          </div>
          <div class="card-body py-2">
            <textarea
              class="form-control form-control-sm"
              rows="4"
              placeholder="Esta información se usa para los asientos contables y también se incluye como 'Descripcion' en la información adicional del comprobante (XML/RIDE)."
              v-model="invoiceFooter.descripcion"
            ></textarea>
          </div>
        </div>

        <!-- FORMA DE PAGO -->
        <div class="card">
          <div class="card-header bg-primary text-white fw-bold py-1">
            Forma de Pago
          </div>
          <div class="card-body py-2">
            <div class="row gy-1 align-items-center">
              <div class="col-md-6">
                <label class="form-label small mb-1">Forma de Pago</label>
                <select
                  class="form-select form-select-sm"
                  v-model="invoiceFooter.formaPago"
                >
                  <option
                    v-for="method in paymentMethods"
                    :key="method.value"
                    :value="method.value"
                  >
                    {{ method.label }}
                  </option>
                </select>
              </div>
              <div class="col-md-6">
                <label class="form-label small mb-1">Valor a Pagar</label>
                <input
                  type="number"
                  class="form-control form-control-sm"
                  v-model.number="invoiceFooter.valorPagar"
                  step="0.01"
                />
              </div>
              <div class="col-md-6 d-flex align-items-center gap-2 mt-2">
                <input
                  type="checkbox"
                  class="form-check-input"
                  id="plazoCheck"
                  v-model="invoiceFooter.pagoPlazo"
                />
                <label class="form-label small mb-0" for="plazoCheck"
                  >¿Pago a Plazo?</label
                >
              </div>
              <div class="col-md-6 mt-2">
                <label class="form-label small mb-1">Plazo (días)</label>
                <input
                  type="number"
                  class="form-control form-control-sm"
                  placeholder="Ej: 30"
                  v-model.number="invoiceFooter.plazoDias"
                  :disabled="!invoiceFooter.pagoPlazo"
                />
              </div>
              <div class="col-12 mt-2">
                <button
                  class="btn btn-outline-secondary w-100 btn-sm"
                  @click="addFormaPago"
                >
                  <i class="bi bi-plus-circle me-1"></i> Añadir
                </button>
              </div>
              <div class="col-12">
                <ul class="list-group list-group-flush small">
                  <li
                    v-for="(fp, idx) in invoiceFooter.formasPagoAgregadas"
                    :key="idx"
                    class="list-group-item d-flex justify-content-between align-items-center py-1"
                  >
                    {{ fp.formaPago }} - ${{ fp.valorPagar.toFixed(2) }} ({{
                      fp.pagoPlazo ? `${fp.plazoDias} días` : "Contado"
                    }})
                    <button
                      class="btn btn-danger btn-sm"
                      @click="removeFormaPago(idx)"
                    >
                      <i class="bi bi-trash"></i>
                    </button>
                  </li>
                </ul>
              </div>
            </div>
          </div>
        </div>
      </div>

      <!-- Columna derecha: Totales -->
      <div class="col-md-6">
        <div class="card h-100">
          <div class="card-header bg-primary text-white fw-bold py-1">
            Totales
          </div>
          <div class="card-body py-2">
            <ul class="list-group list-group-flush small">
              <!-- Subtotales por tipo de IVA -->
              <li v-if="subtotalIVA15 > 0" class="list-group-item py-1 d-flex justify-content-between" :class="themeClass" style="color: var(--text-color); background-color: var(--bg-color); border-color: var(--border-color);">
                Subtotal 15%
                <span style="color: var(--text-color); font-weight: 500;">${{ subtotalIVA15.toFixed(2) }}</span>
              </li>
              <li v-if="subtotalIVA5 > 0" class="list-group-item py-1 d-flex justify-content-between" :class="themeClass" style="color: var(--text-color); background-color: var(--bg-color); border-color: var(--border-color);">
                Subtotal 5%
                <span style="color: var(--text-color); font-weight: 500;">${{ subtotalIVA5.toFixed(2) }}</span>
              </li>
              <li v-if="subtotal0 > 0" class="list-group-item py-1 d-flex justify-content-between" :class="themeClass" style="color: var(--text-color); background-color: var(--bg-color); border-color: var(--border-color);">
                Subtotal 0%
                <span style="color: var(--text-color); font-weight: 500;">${{ subtotal0.toFixed(2) }}</span>
              </li>
              <li v-if="subtotalNoObjetoIVA > 0" class="list-group-item py-1 d-flex justify-content-between" :class="themeClass" style="color: var(--text-color); background-color: var(--bg-color); border-color: var(--border-color);">
                Subtotal no objeto de IVA
                <span style="color: var(--text-color); font-weight: 500;">${{ subtotalNoObjetoIVA.toFixed(2) }}</span>
              </li>
              <li v-if="subtotalExento > 0" class="list-group-item py-1 d-flex justify-content-between" :class="themeClass" style="color: var(--text-color); background-color: var(--bg-color); border-color: var(--border-color);">
                Subtotal exento de IVA
                <span style="color: var(--text-color); font-weight: 500;">${{ subtotalExento.toFixed(2) }}</span>
              </li>
              
              <!-- Separador -->
              <li class="list-group-item py-1 d-flex justify-content-between border-top" :class="themeClass" style="color: var(--text-color); background-color: var(--bg-color); border-color: var(--border-color); border-top: 2px solid var(--border-color) !important;">
                Subtotal sin impuestos
                <span style="color: var(--text-color); font-weight: 500;">${{ totalSinImpuestos.toFixed(2) }}</span>
              </li>
              <li class="list-group-item py-1 d-flex justify-content-between" :class="themeClass" style="color: var(--text-color); background-color: var(--bg-color); border-color: var(--border-color);">
                Total Descuento
                <span style="color: var(--text-color); font-weight: 500;">${{ totalDescuento.toFixed(2) }}</span>
              </li>
              <li v-if="totalICE > 0" class="list-group-item py-1 d-flex justify-content-between" :class="themeClass" style="color: var(--text-color); background-color: var(--bg-color); border-color: var(--border-color);">
                Valor ICE
                <span style="color: var(--text-color); font-weight: 500;">${{ totalICE.toFixed(2) }}</span>
              </li>
              <li v-if="valorIVA15 > 0" class="list-group-item py-1 d-flex justify-content-between" :class="themeClass" style="color: var(--text-color); background-color: var(--bg-color); border-color: var(--border-color);">
                IVA 15%
                <span style="color: var(--text-color); font-weight: 500;">${{ valorIVA15.toFixed(2) }}</span>
              </li>
              <li v-if="valorIVA5 > 0" class="list-group-item py-1 d-flex justify-content-between" :class="themeClass" style="color: var(--text-color); background-color: var(--bg-color); border-color: var(--border-color);">
                IVA 5%
                <span style="color: var(--text-color); font-weight: 500;">${{ valorIVA5.toFixed(2) }}</span>
              </li>
              <li class="list-group-item py-1 d-flex justify-content-between align-items-center" :class="themeClass" style="color: var(--text-color); background-color: var(--bg-color); border-color: var(--border-color);">
                <div class="form-check form-check-inline mb-0">
                  <input class="form-check-input" type="checkbox" id="checkPropina" v-model="aplicarPropina">
                  <label class="form-check-label" for="checkPropina">Propina (10%):</label>
                </div>
                <span style="color: var(--text-color); font-weight: 500;">${{ valorPropina.toFixed(2) }}</span>
              </li>
              
              <!-- Total Final -->
              <li
                class="list-group-item py-2 d-flex justify-content-between fw-bold fs-6 border-top"
                :class="themeClass"
                style="color: var(--text-color); background-color: var(--bg-color); border-color: var(--border-color); border-top: 2px solid var(--border-color) !important;"
              >
                VALOR TOTAL
                <span style="color: var(--text-color); font-weight: bold;">${{ valorTotal.toFixed(2) }}</span>
              </li>
            </ul>
          </div>
        </div>
      </div>
    </div>

    <!-- BOTONES -->
    <div class="mt-3 d-flex gap-3 justify-content-between">
      <div class="d-flex gap-2">
        <button
          class="btn btn-outline-secondary btn-sm px-3"
          @click="nuevaFactura"
          title="Crear nueva factura sin guardar la actual"
        >
          <i class="bi bi-plus-circle me-1"></i>
          Nueva Factura
        </button>

        <!-- Dropdown de borradores -->
        <div class="dropdown" v-if="borradoresDisponibles.length > 0">
          <button
            class="btn btn-outline-info btn-sm px-3 dropdown-toggle"
            type="button"
            @click="mostrarBorradores = !mostrarBorradores"
            title="Borradores disponibles"
          >
            <i class="bi bi-folder me-1"></i>
            Borradores ({{ borradoresDisponibles.length }})
          </button>
          <ul class="dropdown-menu" :class="{ 'show': mostrarBorradores }">
            <li v-for="borrador in borradoresDisponibles" :key="borrador.id">
              <a
                class="dropdown-item"
                href="#"
                @click.prevent="cargarBorrador(borrador.id); mostrarBorradores = false"
              >
                <small class="text-muted d-block">
                  {{ new Date(borrador.created_at).toLocaleString() }}
                </small>
                <strong>{{ borrador.draft_name }}</strong>
                <div class="small text-muted">{{ borrador.draft_description }}</div>
                <span class="badge bg-secondary ms-2">${{ borrador.total_amount.toFixed(2) }}</span>
              </a>
            </li>
            <li><hr class="dropdown-divider"></li>
            <li>
              <a
                class="dropdown-item text-danger"
                href="#"
                @click.prevent="limpiarTodosBorradores(); mostrarBorradores = false"
              >
                <i class="bi bi-trash me-1"></i>
                Limpiar todos los borradores
              </a>
            </li>
          </ul>
        </div>
      </div>
      <div class="d-flex gap-3">
        <button
          class="btn btn-success btn-sm px-4"
          @click="guardarFactura(false)"
          :disabled="procesandoFactura"
        >
          <span v-if="procesandoFactura" class="spinner-border spinner-border-sm me-1" role="status"></span>
          Solo Guardar
        </button>
        <button
          class="btn btn-primary btn-sm px-4"
          @click="guardarFactura(true)"
          :disabled="procesandoFactura"
        >
          <span v-if="procesandoFactura" class="spinner-border spinner-border-sm me-1" role="status"></span>
          Guardar, Firmar y Enviar
        </button>
      </div>
    </div>

    </fieldset>

    <!-- Modal Crear Nuevo Cliente -->
    <transition name="ncm-fade">
      <div v-if="mostrarModalCliente" class="ncm-backdrop" @mousedown.self="cerrarModalNuevoCliente">
        <div class="ncm-panel" role="dialog" aria-modal="true">

          <!-- Cabecera -->
          <div class="ncm-cabecera">
            <span><i class="bi bi-person-plus me-2"></i>Crear nuevo cliente</span>
            <button class="ncm-cerrar" @click="cerrarModalNuevoCliente" title="Cerrar">
              <i class="bi bi-x-lg"></i>
            </button>
          </div>

          <!-- Cuerpo -->
          <div class="ncm-cuerpo">
            <div class="ncm-form-grid">

              <div class="ncm-campo">
                <label class="ncm-etiqueta">Tipo de identificación <span class="ncm-req">*</span></label>
                <select class="ncm-select" v-model="nuevoCliente.identification_type_sri" required>
                  <option value="">Seleccione</option>
                  <option v-for="type in sriTypes" :key="type.code" :value="type.code">
                    {{ type.name }}
                  </option>
                </select>
              </div>

              <div class="ncm-campo">
                <label class="ncm-etiqueta">Número de identificación <span class="ncm-req">*</span></label>
                <div class="ncm-input-wrap">
                  <input
                    type="text"
                    class="ncm-input"
                    v-model="nuevoCliente.identification_number"
                    placeholder="RUC / Cédula / Pasaporte"
                    @blur="buscarEnCatastroSRI"
                    required
                  />
                  <span v-if="buscandoEnCatastro" class="ncm-spinner">
                    <span class="spinner-border spinner-border-sm" role="status"></span>
                  </span>
                </div>
              </div>

              <div class="ncm-campo ncm-campo--full">
                <label class="ncm-etiqueta">Razón social / Nombres <span class="ncm-req">*</span></label>
                <input
                  type="text"
                  class="ncm-input"
                  v-model="nuevoCliente.business_name"
                  placeholder="Nombre completo o razón social"
                  required
                />
              </div>

              <div class="ncm-campo">
                <label class="ncm-etiqueta">Correo electrónico <span class="ncm-req">*</span></label>
                <input
                  type="email"
                  class="ncm-input"
                  v-model="nuevoCliente.email"
                  placeholder="correo@ejemplo.com"
                  required
                />
              </div>

              <div class="ncm-campo">
                <label class="ncm-etiqueta">Teléfono</label>
                <input
                  type="text"
                  class="ncm-input"
                  v-model="nuevoCliente.phone"
                  placeholder="Teléfono de contacto"
                />
              </div>

              <div class="ncm-campo ncm-campo--full">
                <label class="ncm-etiqueta">Dirección</label>
                <input
                  type="text"
                  class="ncm-input"
                  v-model="nuevoCliente.address"
                  placeholder="Dirección completa"
                />
              </div>

            </div>
          </div><!-- /cuerpo -->

          <!-- Acciones -->
          <div class="ncm-acciones">
            <button type="button" class="ncm-btn ncm-btn--cancelar" @click="cerrarModalNuevoCliente" :disabled="guardandoCliente">
              Cancelar
            </button>
            <button type="button" class="ncm-btn ncm-btn--guardar" @click="guardarNuevoCliente" :disabled="guardandoCliente">
              <span v-if="guardandoCliente" class="spinner-border spinner-border-sm me-1"></span>
              <i v-else class="bi bi-check-circle me-1"></i>
              {{ guardandoCliente ? 'Guardando...' : 'Guardar cliente' }}
            </button>
          </div>

        </div>
      </div>
    </transition>
  </div>

  <!-- Modal de Confirmación Personalizado -->
  <div class="modal fade" id="confirmModal" tabindex="-1" aria-labelledby="confirmModalLabel" aria-hidden="true" ref="confirmModal">
    <div class="modal-dialog modal-dialog-centered">
      <div class="modal-content">
        <div class="modal-header">
          <h5 class="modal-title" id="confirmModalLabel">{{ confirmTitle }}</h5>
          <button type="button" class="btn-close" data-bs-dismiss="modal" aria-label="Close"></button>
        </div>
        <div class="modal-body">
          <p class="mb-0">{{ confirmMessage }}</p>
        </div>
        <div class="modal-footer">
          <button type="button" class="btn btn-secondary" data-bs-dismiss="modal" @click="confirmCallback(false)">
            {{ confirmCancelText }}
          </button>
          <button type="button" class="btn btn-primary" @click="confirmCallback(true)">
            {{ confirmAcceptText }}
          </button>
        </div>
      </div>
    </div>
  </div>

  <!-- --------------------------------------------------------------
       Modal: Cobro de factura (se muestra antes del ticket)
  --------------------------------------------------------------- -->
  <div v-if="modalCobro.visible" class="modal-overlay" style="z-index:1060">
    <div class="modal-container" style="max-width:460px; width:95%;">
      <div class="modal-content">
        <div class="modal-header bg-success text-white py-2 px-3 d-flex justify-content-between align-items-center">
          <h6 class="modal-title mb-0">
            <i class="bi bi-cash-coin me-1"></i> Cobro de Factura
          </h6>
        </div>
        <div class="modal-body p-3">

          <!-- Anticipo de proforma (solo si existe) -->
          <div v-if="modalCobro.anticipoProforma > 0" class="alert alert-info py-2 small mb-2">
            <div class="d-flex justify-content-between">
              <span><i class="bi bi-file-earmark-text me-1"></i>Proforma <strong>{{ modalCobro.numeroProforma }}</strong></span>
            </div>
            <div class="d-flex justify-content-between mt-1">
              <span class="text-muted">Total factura:</span>
              <span>${{ parseFloat(modalCobro.totalFactura).toFixed(2) }}</span>
            </div>
            <div class="d-flex justify-content-between">
              <span class="text-muted">Anticipo cobrado:</span>
              <span class="text-success">- ${{ parseFloat(modalCobro.anticipoProforma).toFixed(2) }}</span>
            </div>
            <hr class="my-1" />
            <div class="d-flex justify-content-between fw-bold">
              <span>Saldo a cobrar:</span>
              <span class="text-primary">${{ parseFloat(modalCobro.total).toFixed(2) }}</span>
            </div>
          </div>

          <!-- Total a cobrar -->
          <div class="d-flex justify-content-between align-items-center cobro-resumen-caja rounded p-2 mb-3">
            <span class="cobro-texto-secundario small fw-semibold">{{ modalCobro.anticipoProforma > 0 ? 'SALDO A COBRAR' : 'TOTAL A COBRAR' }}</span>
            <span class="fs-4 fw-bold cobro-total-valor">${{ parseFloat(modalCobro.total).toFixed(2) }}</span>
          </div>

          <div class="alert alert-danger py-2 small" v-if="modalCobro.error">{{ modalCobro.error }}</div>

          <!-- Método de cobro -->
          <div class="mb-3">
            <label class="form-label fw-semibold small">Método de cobro</label>
            <div class="d-flex gap-2">
              <button
                type="button"
                :class="['btn btn-sm flex-fill', modalCobro.metodo === 'efectivo' ? 'btn-success' : 'btn-outline-secondary']"
                @click="modalCobro.metodo = 'efectivo'; modalCobro.destinoId = cajaActiva?.caja_id ?? ''"
              >
                <i class="bi bi-cash me-1"></i> Efectivo (Caja)
              </button>
              <button
                type="button"
                :class="['btn btn-sm flex-fill', modalCobro.metodo === 'transferencia' ? 'btn-primary' : 'btn-outline-secondary']"
                @click="modalCobro.metodo = 'transferencia'; modalCobro.destinoId = bancos[0]?.id ?? ''"
              >
                <i class="bi bi-bank me-1"></i> Transferencia (Banco)
              </button>
            </div>
          </div>

          <!-- EFECTIVO: caja propia (fija, no se elige la de otro usuario) + monto recibido + vuelto -->
          <template v-if="modalCobro.metodo === 'efectivo'">
            <div class="mb-3">
              <label class="form-label fw-semibold small">Caja destino</label>
              <div v-if="cajaActiva" class="form-control form-control-sm bg-body-secondary">
                {{ cajaActiva.nombre_caja }}
              </div>
              <div v-else class="form-text text-warning">No tiene una caja propia abierta</div>
            </div>
            <div class="mb-3">
              <label class="form-label fw-semibold small">Monto recibido ($)</label>
              <input
                v-model.number="modalCobro.recibido"
                type="number" step="0.01" min="0"
                class="form-control form-control-sm"
                @input="modalCobro.error = ''"
              />
            </div>
            <div class="d-flex justify-content-between align-items-center px-1 mb-1">
              <span class="small text-muted">Vuelto / Cambio</span>
              <span
                class="fw-bold fs-5"
                :class="vuelto >= 0 ? 'text-success' : 'text-danger'"
              >
                ${{ vuelto >= 0 ? vuelto.toFixed(2) : Math.abs(vuelto).toFixed(2) }}
              </span>
            </div>
          </template>

          <!-- TRANSFERENCIA: destino banco + número de transferencia -->
          <template v-if="modalCobro.metodo === 'transferencia'">
            <div class="mb-3">
              <label class="form-label fw-semibold small">Banco destino</label>
              <select v-model="modalCobro.destinoId" class="form-select form-select-sm">
                <option value="">-- Seleccionar banco --</option>
                <option v-for="b in bancos" :key="b.id" :value="b.id">{{ b.nombre_banco }} — {{ b.numero_cuenta }}</option>
              </select>
              <div v-if="bancos.length === 0" class="form-text text-warning">No hay cuentas bancarias configuradas</div>
            </div>
            <div class="mb-3">
              <label class="form-label fw-semibold small">N.° de transferencia <span class="text-muted">(respaldo)</span></label>
              <input
                v-model="modalCobro.numTransferencia"
                type="text"
                class="form-control form-control-sm"
                placeholder="Número de comprobante de la transferencia"
              />
            </div>
          </template>

        </div>
        <div class="modal-footer py-2 px-3 d-flex justify-content-end gap-2">
          <button
            type="button"
            class="btn btn-success btn-sm"
            :disabled="modalCobro.guardando"
            @click="confirmarCobro"
          >
            <span v-if="modalCobro.guardando" class="spinner-border spinner-border-sm me-1"></span>
            <i v-else class="bi bi-check-circle me-1"></i>
            {{ modalCobro.guardando ? 'Registrando...' : 'Cobrar e imprimir ticket' }}
          </button>
        </div>
      </div>
    </div>
  </div>

  <!-- Modal: Impresión de Ticket Térmico -->
  <div v-if="modalTicket.visible" class="modal-overlay" @click.self="modalTicket.visible = false">
    <div class="modal-container" style="max-width:420px; width:95%;">
      <div class="modal-content">
        <div class="modal-header bg-dark text-white py-2 px-3 d-flex justify-content-between align-items-center">
          <h6 class="modal-title mb-0">
            <i class="bi bi-printer me-1"></i> Ticket Térmico
          </h6>
          <button
            type="button"
            class="btn-close btn-close-white btn-sm"
            @click="modalTicket.visible = false"
          ></button>
        </div>
        <div class="modal-body p-0" style="background:#fff; overflow:hidden;">
          <iframe
            ref="iframeTicket"
            :src="modalTicket.html"
            style="width:100%; height:520px; border:none; display:block;"
          ></iframe>
        </div>
        <div class="modal-footer py-2 px-3 d-flex justify-content-end gap-2">
          <button
            type="button"
            class="btn btn-secondary btn-sm"
            @click="modalTicket.visible = false"
          >Cerrar</button>
          <button
            v-if="esAndroid"
            type="button"
            class="btn btn-outline-info btn-sm"
            @click="descargarTicketAndroid"
            title="Abre el ticket en el navegador para imprimir directamente"
          >
            <i class="bi bi-download"></i> Abrir en navegador
          </button>
          <button
            type="button"
            class="btn btn-primary btn-sm"
            @click="imprimirDesdeModal"
          >
            <i class="bi bi-printer"></i> Imprimir
          </button>
        </div>
      </div>
    </div>
  </div>

  <!-- Modal editar cliente -->
  <ModalEditarCliente
    :visible="modalEditarClienteVisible"
    :clienteId="buyer.clientId || null"
    @cerrar="modalEditarClienteVisible = false"
    @guardado="alGuardarCliente"
  />

</template>

<style scoped>
/* ── Avisos del superadmin (banner descartable) ── */
.aviso-sistema {
  background: #ffffff;
  color: #000000;
  border: 1px solid #dee2e6;
  border-radius: 0.375rem;
}
.aviso-sistema span { white-space: pre-wrap; }
.aviso-sistema__tipo,
.aviso-sistema__icono { color: #0d6efd; }

/* ── Modo solo lectura (factura ya no editable, vista desde Documentos Emitidos) ── */
.borde-fieldset-reset {
  border: 0;
  margin: 0;
  padding: 0;
  min-width: 0;
}
.campos-solo-lectura {
  pointer-events: none;
  opacity: 0.85;
  user-select: text;
}

.modal-overlay {
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background-color: rgba(0, 0, 0, 0.5);
  display: flex;
  justify-content: center;
  align-items: center;
  z-index: 1050;
}
.modal-container {
  width: 90%;
  max-width: 700px;
  max-height: 90vh;
  overflow-y: auto;
  border-radius: 8px;
}
.modal-content {
  border-radius: 8px;
  box-shadow: 0 5px 15px rgba(0,0,0,0.3);
}

/* Modal — variables del sistema claro/oscuro */
.modal-container .modal-content {
  background: var(--secondary-bg-main);
  color: var(--text-primary);
}
.modal-container .modal-body {
  background: var(--secondary-bg-main);
}
.modal-container .modal-footer {
  background: var(--bg-main);
  border-top: 1px solid var(--border-main);
}
.modal-container .form-control,
.modal-container .form-select {
  background-color: var(--secondary-bg-main);
  color: var(--text-primary);
  border-color: var(--border-main);
}
.modal-container .form-control:focus,
.modal-container .form-select:focus {
  border-color: var(--border-focus);
  box-shadow: 0 0 0 2px color-mix(in srgb, var(--border-focus) 20%, transparent);
  outline: none;
}
.modal-container .form-control::placeholder {
  color: var(--text-muted);
}
.modal-container .form-label {
  color: var(--text-primary);
}
.modal-container .form-text {
  color: var(--text-secondary);
}

/* ══════════════════════════════════════════════════════════════
   MODAL: Crear Nuevo Cliente — línea gráfica unificada con
   ModalEditarCliente.vue (backdrop oscuro, cabecera con color
   primario, grid de formulario, botones consistentes)
══════════════════════════════════════════════════════════════ */
.ncm-backdrop {
  position: fixed;
  inset: 0;
  background: rgba(0, 0, 0, .55);
  z-index: 1055;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 1rem;
}
.ncm-panel {
  background: var(--secondary-bg-main);
  color: var(--text-primary);
  border: 1px solid var(--border-main);
  border-radius: .6rem;
  box-shadow: var(--shadow-md);
  width: 100%;
  max-width: 620px;
  max-height: 90vh;
  overflow-y: auto;
}
.ncm-cabecera {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: .7rem 1rem;
  background: var(--primary-main);
  color: #fff;
  font-size: .9rem;
  font-weight: 600;
  border-radius: .6rem .6rem 0 0;
}
.ncm-cerrar {
  background: none;
  border: none;
  color: rgba(255,255,255,.8);
  cursor: pointer;
  padding: .2rem .4rem;
  border-radius: .3rem;
  transition: background .12s;
}
.ncm-cerrar:hover { background: rgba(255,255,255,.2); color: #fff; }

.ncm-cuerpo { padding: 1rem; }

.ncm-form-grid {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: .75rem;
}
.ncm-campo { display: flex; flex-direction: column; gap: .25rem; }
.ncm-campo--full { grid-column: 1 / -1; }

.ncm-etiqueta { font-size: .79rem; color: var(--text-secondary); font-weight: 500; }
.ncm-req { color: var(--accent-danger); }

.ncm-input,
.ncm-select {
  background: var(--bg-main);
  color: var(--text-primary);
  border: 1px solid var(--border-main);
  border-radius: .375rem;
  padding: .4rem .65rem;
  font-size: .85rem;
  width: 100%;
  outline: none;
  transition: border-color .15s;
}
.ncm-input:focus,
.ncm-select:focus {
  border-color: var(--border-focus);
  box-shadow: 0 0 0 2px color-mix(in srgb, var(--border-focus) 20%, transparent);
}
.ncm-input::placeholder { color: var(--text-muted); }

.ncm-input-wrap { position: relative; display: flex; align-items: center; }
.ncm-spinner {
  position: absolute;
  right: .6rem;
  color: var(--primary-main);
  display: inline-flex;
}

.ncm-acciones {
  display: flex;
  gap: .6rem;
  justify-content: flex-end;
  padding: .75rem 1rem;
  border-top: 1px solid var(--border-main);
  background: var(--bg-main);
  border-radius: 0 0 .6rem .6rem;
}
.ncm-btn {
  padding: .45rem 1rem;
  font-size: .84rem;
  font-weight: 500;
  border-radius: .375rem;
  cursor: pointer;
  border: none;
  display: inline-flex;
  align-items: center;
  transition: background .15s, opacity .15s;
}
.ncm-btn:disabled { opacity: .55; cursor: not-allowed; }
.ncm-btn--cancelar {
  background: transparent;
  border: 1px solid var(--border-main);
  color: var(--text-secondary);
}
.ncm-btn--cancelar:hover:not(:disabled) { background: var(--border-main); }
.ncm-btn--guardar {
  background: var(--primary-main);
  color: #fff;
}
.ncm-btn--guardar:hover:not(:disabled) { background: var(--primary-hover); }

.ncm-fade-enter-active,
.ncm-fade-leave-active { transition: opacity .2s ease; }
.ncm-fade-enter-from,
.ncm-fade-leave-to { opacity: 0; }

@media (max-width: 576px) {
  .ncm-form-grid { grid-template-columns: 1fr; }
}
/* Caja de resumen "Total a cobrar" */
.cobro-resumen-caja {
  background: var(--bg-main);
  border: 1px solid var(--border-main);
}
.cobro-texto-secundario {
  color: var(--text-secondary);
}
.cobro-total-valor {
  color: var(--accent-success);
}

/* Estilos para lista de sugerencias */
.sugerencias-lista {
  z-index: 1000;
  max-height: 250px;
  overflow-y: auto;
  border: 1px solid #dee2e6;
  border-top: none;
  border-radius: 0 0 4px 4px;
}
.sugerencias-lista .list-group-item {
  cursor: pointer;
  border-left: none;
  border-right: none;
}
.sugerencias-lista .list-group-item:first-child {
  border-top: none;
}
.sugerencias-lista .list-group-item:hover,
.sugerencias-lista .list-group-item.active {
  background-color: #0d6efd;
  color: white;
}
.sugerencias-lista .list-group-item:hover small,
.sugerencias-lista .list-group-item.active small {
  color: rgba(255,255,255,0.8) !important;
}

/* Tema oscuro para sugerencias */
.dark-theme .sugerencias-lista {
  background-color: #2d2d2d;
  border-color: #555;
}
.dark-theme .sugerencias-lista .list-group-item {
  background-color: #2d2d2d;
  color: #e0e0e0;
  border-color: #444;
}
.dark-theme .sugerencias-lista .list-group-item:hover,
.dark-theme .sugerencias-lista .list-group-item.active {
  background-color: #0d6efd;
  color: white;
}

.stock-inline-linea {
  font-size: 0.72rem;
  font-weight: 500;
  margin-top: 2px;
}
.stock-inline-linea--ok {
  color: var(--accent-success, #198754);
}
.stock-inline-linea--warn {
  color: var(--accent-danger, #dc3545);
  font-weight: 600;
}
</style>

<script>
import api from "@/services/api";
import themeManager from '@/store/theme'
import { Modal } from 'bootstrap'
import notificationService from '@/services/notificationService'
import ModalEditarCliente from '@/components/ModalEditarCliente.vue'
import { cerrarSesion } from '@/services/servicioAutenticacion'
import controlAcceso from '@/services/controlAccesoService'
import { fechaIsoSistema } from '@/utils/fechaSistema'

export default {
  name: "ClientVentas",
  components: { ModalEditarCliente },
  data() {
    return {
      auth: {
        userId: null,
        companyId: null,
      },
      modoDescuento: 'bloqueado', // 'bloqueado' | 'directo' | 'aprobacion' (segun el rol del usuario)
      // Encabezado de la factura
      invoiceHeader: {
        fecha: this.getCurrentEcuadorDate(), // Inicializar con fecha actual
        puntoEmision: "", // Se cargará dinámicamente
        secuencial: "00000001", // deberia venir de backend; aquí placeholder
        ambiente: "", // Se carga dinámicamente desde /configuracion/ambiente-sri
        sucursal: "Principal",
        vendedor: "Sin asignar",
        bodega: "Bodega Principal",
        esCreditoInterno: false,
        diasCredito: 30,
      },
      alertaDeuda: { mostrar: false, cantidad: 0, montoTotal: 0 },
      cajaActiva: null,
      // Datos del comprador
      buyer: {
        clientId: null,
        identificacion: "",
        tipoIdentificacion: "",
        razonSocial: "",
        telefono: "",
        celular: "",
        correo: "",
        direccion: "",
      },
      clienteModificadoEnFactura: false,
      loadingSRI: false, // se usa como "buscando cliente"
      // Datos para añadir un producto temporalmente
      currentProduct: {
        id: null,
        codigo: "",
        nombre: "",
        cantidad: 1,
        precioUnitario: 0,
        descuento: 0,
        porcentajeIVA: 15,
        taxCode: '2', // 2=IVA 15%, 3=IVA 5%, 0=IVA 0%, 6=No objeto, 7=Exento
        porcentajeICE: 0,
        aplicaIVA: true,
      },
      loadingProductSearch: false,
      productSearchError: "",
      productSearchTimeout: null,
      productSearchQuery: "",
      productSuggestions: [],
      cargasProductoEnProceso: new Map(),
      // Lista de productos añadidos a la factura
      productsInInvoice: [],
      // Pie de página de la factura
      invoiceFooter: {
        descripcion: "",
        formaPago: "SIN UTILIZACION DEL SISTEMA FINANCIERO",
        valorPagar: 0,
        pagoPlazo: false,
        plazoDias: 0,
        formasPagoAgregadas: [],
      },
      originalBuyerData: null,
      sriTypes: [],
      loadingSriTypes: true,
      emissionPoints: [],
      loadingEmissionPoints: false,
      buscandoCliente: false,
      modalEditarClienteVisible: false,
      sucursales: [],
      loadingSucursales: false,
      vendedores: [],
      loadingVendedores: false,
      almacenes: [],
      loadingAlmacenes: false,
      // Intervalos para recarga automática
      sucursalesReloadInterval: null,
      // Autocompletado de clientes
      sugerenciasClientes: [],
      mostrarSugerencias: false,
      indiceSugerenciaActiva: -1,
      debounceTimer: null,
      // Modal nuevo cliente
      mostrarModalCliente: false,
      guardandoCliente: false,
      buscandoEnCatastro: false,
      nuevoCliente: {
        identification_type_sri: '',
        identification_number: '',
        business_name: '',
        email: '',
        phone: '',
        address: ''
      },
      // Control de borradores
      mostrarBorradores: false,
      // Modal de confirmación
      confirmTitle: '',
      confirmMessage: '',
      confirmAcceptText: 'Aceptar',
      confirmCancelText: 'Cancelar',
      confirmCallback: null,
      // Datos de borradores desde API
      borradoresDisponiblesData: [],
      // Códigos SRI para formas de pago
      sriPaymentCodes: {},
      paymentMethods: [], // Opciones dinámicas de formas de pago
      // Método de pago seleccionado
      paymentMethod: "01", // Efectivo por defecto
      // Propina
      aplicarPropina: false,
      // ID de factura que se está editando (null = modo creación)
      facturaEditandoId: null,
      // true = factura cargada desde Documentos Emitidos solo para visualizar
      // (ya no es editable: firmada, enviada, autorizada, anulada, etc.)
      soloLectura: false,
      // Avisos del superadmin vigentes hoy, aun no descartados en este navegador
      notificacionesActivas: [],
      // Bloqueo durante guardado/firma para evitar doble envío
      procesandoFactura: false,
      // Modal de impresión de ticket térmico (reemplaza window.open)
      modalTicket: {
        visible: false,
        html: '',
        anchoMm: 80,
      },
      // Modal de cobro (se muestra antes del ticket al guardar+firmar)
      modalCobro: {
        visible:   false,
        facturaId: null,
        autorizada: false,
        promesaFirma: null,  // Promise de la firma SRI (corre en paralelo)
        total:     0,
        recibido:  0,
        metodo:    'efectivo',   // efectivo | transferencia
        destinoId: '',
        numTransferencia: '',
        error:     '',
        guardando: false,
        resolve:   null,  // Promise resolve para esperar confirmacion
      },
      bancos: [],
      // Datos de proforma origen (si la factura viene de una proforma)
      proformaOrigen: null, // { proforma_id, total_anticipos, saldo_pendiente, numero_proforma }
      // Datos de pedido origen (si la factura viene de un pedido)
      pedidoOrigen: null, // { pedido_id, numero_pedido, ya_pagado }
      ordenTallerOrigen: null, // { orden_taller_id, numero_orden }
      esAndroid: /Android/i.test(navigator.userAgent),
    };
  },
  computed: {
    themeClass() {
      return themeManager.themeClass
    },
    isPlanBasico() {
      return this.$store?.getters?.isPlanBasico === true
    },
    borradoresDisponibles() {
      return [...this.borradoresDisponiblesData].sort((a, b) => new Date(b.created_at) - new Date(a.created_at));
    },
    // Vuelto calculado en tiempo real para el modal de cobro
    vuelto() {
      const recibido = parseFloat(this.modalCobro.recibido) || 0;
      return Number((recibido - this.modalCobro.total).toFixed(2));
    },
    totalSinImpuestos() {
      const total = this.productsInInvoice.reduce(
        (sum, p) => sum + p.subtotalSinImpuestos,
        0
      );
      return Number(total.toFixed(2));
    },
    // Agrupación de productos por código de impuesto SRI
    productosPorImpuesto() {
      const grupos = {};
      this.productsInInvoice.forEach(p => {
        const taxCode = p.taxCode || '2'; // Default IVA 15%
        if (!grupos[taxCode]) {
          grupos[taxCode] = {
            baseImponible: 0,
            valorImpuesto: 0
          };
        }
        grupos[taxCode].baseImponible += p.subtotalSinImpuestos;
        grupos[taxCode].valorImpuesto += p.iva;
      });
      return grupos;
    },
    subtotalIVA15() {
      return Number((this.productosPorImpuesto['2']?.baseImponible || 0).toFixed(2));
    },
    subtotalIVA5() {
      return Number((this.productosPorImpuesto['3']?.baseImponible || 0).toFixed(2));
    },
    subtotalIVA() {
      // Mantener para compatibilidad - suma IVA 15% + 5%
      return Number((this.subtotalIVA15 + this.subtotalIVA5).toFixed(2));
    },
    subtotal0() {
      return Number((this.productosPorImpuesto['0']?.baseImponible || 0).toFixed(2));
    },
    subtotalNoObjetoIVA() {
      return Number((this.productosPorImpuesto['6']?.baseImponible || 0).toFixed(2));
    },
    subtotalExento() {
      return Number((this.productosPorImpuesto['7']?.baseImponible || 0).toFixed(2));
    },
    totalDescuento() {
      const total = this.productsInInvoice.reduce(
        (sum, p) => sum + p.descuentoTotal,
        0
      );
      return Number(total.toFixed(2));
    },
    totalICE() {
      const total = this.productsInInvoice.reduce((sum, p) => sum + p.ice, 0);
      return Number(total.toFixed(2));
    },
    totalIVA() {
      const total = this.productsInInvoice.reduce((sum, p) => sum + p.iva, 0);
      return Number(total.toFixed(2));
    },
    valorIVA15() {
      return Number((this.productosPorImpuesto['2']?.valorImpuesto || 0).toFixed(2));
    },
    valorIVA5() {
      return Number((this.productosPorImpuesto['3']?.valorImpuesto || 0).toFixed(2));
    },
    valorPropina() {
      if (!this.aplicarPropina) return 0;
      return Number((this.totalSinImpuestos * 0.10).toFixed(2));
    },
    valorTotal() {
      const total = this.productsInInvoice.reduce((sum, p) => sum + p.total, 0);
      return Number((total + this.valorPropina).toFixed(2));
    },
  },
  watch: {
    valorTotal: {
      handler(newValue) {
        this.invoiceFooter.valorPagar = newValue;
      },
      immediate: true,
    },
    'invoiceHeader.puntoEmision': {
      async handler(newValue) {
        if (newValue) {
          // En modo edición no sobreescribir el secuencial de la factura original
          if (!this.facturaEditandoId) {
            await this.loadSequenceForEmissionPoint(newValue);
          }
          // También recargar sucursales por si cambió la configuración
          await this.reloadSucursales();
          
          // Actualizar sucursal basada en el punto de emisión seleccionado
          const selectedPoint = this.emissionPoints.find(p => p.emission_point_code === newValue);
          if (selectedPoint) {
            // Extraer establishment_code del full_code (formato: establishment-emission)
            const establishmentCode = selectedPoint.full_code.split('-')[0];
            const sucursal = this.sucursales.find(s => s.codigo === establishmentCode);
            if (sucursal) {
              this.invoiceHeader.sucursal = sucursal.nombre;
              console.log(`Sucursal actualizada automáticamente a ${sucursal.nombre} (${sucursal.codigo}) por cambio de punto de emisión`);
            }
          }
        }
      },
    },
  },
  methods: {
    cerrarSesionSiNoEresTu() {
      const confirmar = confirm(
        `Estas a punto de cerrar la sesion de "${this.invoiceHeader.vendedor}". Continua solo si NO eres tu quien esta usando este dispositivo.`
      )
      if (confirmar) cerrarSesion()
    },

    onCambioTipoIdentificacion() {
      this.marcarClienteModificado();
      if (this.buyer.tipoIdentificacion === '07') {
        this.buyer.identificacion  = '9999999999999';
        this.buyer.razonSocial     = 'CONSUMIDOR FINAL';
        this.buyer.telefono        = '';
        this.buyer.celular         = '';
        this.buyer.correo          = 'sin.correo@sicecom.com';
        this.buyer.direccion       = 'SD';
        this.buyer.clientId        = null;
      }
    },
    async buscarClienteAutomatico(tipoBusqueda) {
      // tipoBusqueda: 'identificacion' o 'nombre'
      const identificacion = this.buyer.identificacion?.trim();
      const nombre = this.buyer.razonSocial?.trim();
      
      // Determinar el valor a buscar
      const valorBusqueda = tipoBusqueda === 'identificacion' ? identificacion : nombre;
      
      // Validar que hay algo que buscar (mínimo 3 caracteres)
      if (!valorBusqueda || valorBusqueda.length < 3) {
        return;
      }
      
      this.buscandoCliente = true;
      
      try {
        // El backend espera parámetros: q (query) y tipo ('identificacion' o 'nombre')
        const { data } = await api.get('/clientes/buscar', { 
          params: {
            q: valorBusqueda,
            tipo: tipoBusqueda
          }
        });
        
        if (data.success && data.data) {
          const cliente = data.data;
          
          // Llenar los datos del comprador
          const numIdentificacion = cliente.identification_number || cliente.numero_ruc || identificacion;
          this.buyer.identificacion = numIdentificacion;
          this.buyer.razonSocial = cliente.business_name || cliente.razon_social || nombre;
          this.buyer.direccion = cliente.address || cliente.direccion_matriz || this.buyer.direccion;
          this.buyer.correo = cliente.email || this.buyer.correo;
          this.buyer.telefono = cliente.phone || this.buyer.telefono;
          
          // Guardar el ID del cliente si viene de la tabla clients
          if (data.source === 'clients' && cliente.id) {
            this.buyer.clientId = cliente.id;
            this.clienteModificadoEnFactura = false;
          }
          
          // Determinar tipo de identificación basado en la longitud
          // 04 = RUC (13 dígitos), 05 = Cédula (10 dígitos), 06 = Pasaporte, 07 = Consumidor Final
          if (cliente.tipo_identificacion) {
            this.buyer.tipoIdentificacion = cliente.tipo_identificacion;
          } else if (numIdentificacion) {
            const len = numIdentificacion.length;
            if (len === 13) {
              this.buyer.tipoIdentificacion = '04'; // RUC
            } else if (len === 10) {
              this.buyer.tipoIdentificacion = '05'; // Cédula
            } else {
              this.buyer.tipoIdentificacion = '06'; // Pasaporte u otro
            }
          }
          
          this.mostrarNotificacion('Cliente encontrado', 'success');
          // Verificar deudas si el cliente viene de la DB
          if (data.source === 'clients' && cliente.id) {
            this.verificarDeudaCliente(cliente.id);
          }
        }
      } catch (error) {
        // Si no se encuentra, no mostrar error, solo log
        console.log('Cliente no encontrado:', error.response?.data?.message || error.message);
      } finally {
        this.buscandoCliente = false;
      }
    },
    // Métodos para autocompletado de clientes
    marcarClienteModificado() {
      if (this.buyer.clientId) {
        this.clienteModificadoEnFactura = true;
      }
    },
    alCambiarNombreComprador() {
      this.marcarClienteModificado();
      this.buscarClientesMientrasEscribe();
    },
    buscarClientesMientrasEscribe() {
      const query = this.buyer.razonSocial?.trim();
      
      // Limpiar timer anterior
      if (this.debounceTimer) {
        clearTimeout(this.debounceTimer);
      }
      
      // Si menos de 2 caracteres, cerrar sugerencias
      if (!query || query.length < 2) {
        this.sugerenciasClientes = [];
        this.mostrarSugerencias = false;
        return;
      }
      
      // Debounce de 300ms para no hacer muchas peticiones
      this.debounceTimer = setTimeout(async () => {
        this.buscandoCliente = true;
        try {
          const { data } = await api.get('/clientes/autocompletar', {
            params: { q: query, limit: 8 }
          });
          
          if (data.success && data.data) {
            this.sugerenciasClientes = data.data;
            this.mostrarSugerencias = true;
            this.indiceSugerenciaActiva = -1;
          } else {
            this.sugerenciasClientes = [];
          }
        } catch (error) {
          console.log('Error en autocompletado:', error.message);
          this.sugerenciasClientes = [];
        } finally {
          this.buscandoCliente = false;
        }
      }, 300);
    },
    seleccionarCliente(cliente) {
      // Llenar todos los campos con los datos del cliente
      this.buyer.clientId = cliente.id;
      this.buyer.identificacion = cliente.identification_number || '';
      this.buyer.tipoIdentificacion = cliente.tipo_identificacion || '';
      this.buyer.razonSocial = cliente.business_name || '';
      this.buyer.correo = cliente.email || '';
      this.buyer.telefono = cliente.phone || '';
      this.buyer.direccion = cliente.address || '';
      this.clienteModificadoEnFactura = false;
      
      // Cerrar sugerencias
      this.cerrarSugerencias();
      this.mostrarNotificacion('Cliente seleccionado', 'success');
      // Verificar deudas pendientes del cliente
      if (cliente.id) {
        this.verificarDeudaCliente(cliente.id);
      }
    },
    alGuardarCliente(datos) {
      // Actualiza los campos del comprador con los datos corregidos en el modal
      this.buyer.razonSocial    = datos.razon_social
      this.buyer.identificacion = datos.numero_identificacion
      this.buyer.tipoIdentificacion = datos.tipo_identificacion
      this.buyer.direccion      = datos.direccion    || ''
      this.buyer.telefono       = datos.telefono     || ''
      this.buyer.correo         = datos.correo       || ''
      this.clienteModificadoEnFactura = false
      this.modalEditarClienteVisible = false
    },
    async verificarDeudaCliente(clienteId) {
      this.alertaDeuda = { mostrar: false, cantidad: 0, montoTotal: 0 };
      try {
        const { data } = await api.get(`/clientes/${clienteId}/deudas`);
        if (data.success && data.data && data.data.cantidad > 0) {
          const monto = parseFloat(data.data.monto_pendiente || 0);
          this.alertaDeuda = {
            mostrar: true,
            cantidad: data.data.cantidad,
            montoTotal: monto,
          };
          this.mostrarNotificacion(
            `Cliente con deuda pendiente: ${data.data.cantidad} factura(s) por $${monto.toFixed(2)}`,
            'warning'
          );
        }
      } catch (error) {
        // Endpoint opcional: si no existe, ignorar silenciosamente
        console.log('Verificación de deudas no disponible:', error.response?.status);
      }
    },
    async verificarCajaActiva() {
      try {
        const { data } = await api.get('/cajas/sesion-activa');
        this.cajaActiva = data.sesion ?? null;
      } catch (error) {
        // Si falla (ej. sin módulo de caja), dejar null para no bloquear innecesariamente
        console.log('Verificación de caja activa no disponible:', error.response?.status);
        this.cajaActiva = undefined; // undefined = no se pudo verificar (no bloquea)
      }
    },
    navegarSugerencia(direccion) {
      if (this.sugerenciasClientes.length === 0) return;
      
      this.indiceSugerenciaActiva += direccion;
      
      // Mantener dentro de límites
      if (this.indiceSugerenciaActiva < 0) {
        this.indiceSugerenciaActiva = this.sugerenciasClientes.length - 1;
      } else if (this.indiceSugerenciaActiva >= this.sugerenciasClientes.length) {
        this.indiceSugerenciaActiva = 0;
      }
    },
    seleccionarSugerenciaActiva() {
      if (this.indiceSugerenciaActiva >= 0 && this.sugerenciasClientes[this.indiceSugerenciaActiva]) {
        this.seleccionarCliente(this.sugerenciasClientes[this.indiceSugerenciaActiva]);
      }
    },
    cerrarSugerencias() {
      this.sugerenciasClientes = [];
      this.mostrarSugerencias = false;
      this.indiceSugerenciaActiva = -1;
    },
    cerrarSugerenciasConDelay() {
      // Delay para permitir click en sugerencia
      setTimeout(() => {
        this.cerrarSugerencias();
      }, 200);
    },
    abrirModalNuevoCliente() {
      // Pre-llenar con la identificación actual si existe
      if (this.buyer.identificacion) {
        this.nuevoCliente.identification_number = this.buyer.identificacion;
        // Determinar tipo según longitud
        const len = this.buyer.identificacion.length;
        if (len === 13) {
          this.nuevoCliente.identification_type_sri = '04';
        } else if (len === 10) {
          this.nuevoCliente.identification_type_sri = '05';
        }
      }
      this.mostrarModalCliente = true;
    },
    cerrarModalNuevoCliente() {
      this.mostrarModalCliente = false;
      this.limpiarFormularioCliente();
    },
    limpiarFormularioCliente() {
      this.nuevoCliente = {
        identification_type_sri: '',
        identification_number: '',
        business_name: '',
        email: '',
        phone: '',
        address: ''
      };
    },
    async buscarEnCatastroSRI() {
      const identificacion = this.nuevoCliente.identification_number?.trim();
      
      // Validar que hay algo que buscar (mínimo 10 caracteres para cédula/RUC)
      if (!identificacion || identificacion.length < 10) {
        return;
      }
      
      this.buscandoEnCatastro = true;
      
      try {
        // Buscar en el catastro del SRI
        const { data } = await api.get(`/sri/ruc-catastro/${identificacion}`);
        
        // El backend retorna los datos directamente o con wrapper
        const catastro = data.data || data;
        
        if (catastro && catastro.numero_ruc) {
          // Llenar los campos con los datos del catastro
          this.nuevoCliente.business_name = catastro.razon_social || catastro.nombre_fantasia_comercial || '';
          this.nuevoCliente.email = catastro.correo_electronico || '';
          this.nuevoCliente.address = catastro.descripcion_provincia_est 
            ? `${catastro.descripcion_provincia_est}, ${catastro.descripcion_canton_est || ''}`
            : '';
          
          // Determinar tipo de identificación según longitud
          if (identificacion.length === 13) {
            this.nuevoCliente.identification_type_sri = '04'; // RUC
          } else if (identificacion.length === 10) {
            this.nuevoCliente.identification_type_sri = '05'; // Cédula
          }
          
          this.mostrarNotificacion('Datos obtenidos del catastro SRI', 'success');
        }
      } catch (error) {
        // Si no se encuentra en catastro, no mostrar error
        console.log('No encontrado en catastro SRI:', error.response?.data?.message || error.message);
      } finally {
        this.buscandoEnCatastro = false;
      }
    },
    async guardarNuevoCliente() {
      // Validar campos requeridos
      if (!this.nuevoCliente.identification_type_sri) {
        this.mostrarNotificacion('Seleccione el tipo de identificación', 'error');
        return;
      }
      if (!this.nuevoCliente.identification_number) {
        this.mostrarNotificacion('Ingrese el número de identificación', 'error');
        return;
      }
      if (!this.nuevoCliente.business_name) {
        this.mostrarNotificacion('Ingrese la razón social o nombres', 'error');
        return;
      }
      if (!this.nuevoCliente.email) {
        this.mostrarNotificacion('Ingrese el correo electrónico', 'error');
        return;
      }
      // Validar formato de correo
      const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
      if (!emailRegex.test(this.nuevoCliente.email)) {
        this.mostrarNotificacion('Ingrese un correo electrónico válido', 'error');
        return;
      }

      this.guardandoCliente = true;

      try {
        const { data } = await api.post('/clientes', this.nuevoCliente);
        
        if (data.id) {
          // Cliente creado exitosamente
          this.mostrarNotificacion('Cliente creado exitosamente', 'success');
          
          // Llenar los datos del comprador con el nuevo cliente
          this.buyer.clientId = data.id;
          this.buyer.identificacion = this.nuevoCliente.identification_number;
          this.buyer.tipoIdentificacion = this.nuevoCliente.identification_type_sri;
          this.buyer.razonSocial = this.nuevoCliente.business_name;
          this.buyer.correo = this.nuevoCliente.email || '';
          this.buyer.telefono = this.nuevoCliente.phone || '';
          this.buyer.direccion = this.nuevoCliente.address || '';
          this.clienteModificadoEnFactura = false;
          
          // Cerrar modal y limpiar formulario
          this.cerrarModalNuevoCliente();
          this.limpiarFormularioCliente();
        }
      } catch (error) {
        const mensaje = error.response?.data?.error || error.response?.data?.message || 'Error al crear cliente';
        this.mostrarNotificacion(mensaje, 'error');
      } finally {
        this.guardandoCliente = false;
      }
    },
    async loadSequenceForEmissionPoint(emissionPointCode) {
      // Encontrar el punto de emisión seleccionado
      const selectedPoint = this.emissionPoints.find(
        p => p.emission_point_code === emissionPointCode
      );
      
      if (!selectedPoint) {
        console.warn('Punto de emisión no encontrado:', emissionPointCode);
        return;
      }

      try {
        const { data } = await api.get('/configuracion/secuencial/next', {
          params: {
            eep_id: selectedPoint.id,
            document_type: '01' // Factura
          }
        });

        if (data.success && data.data) {
          // Formatear el secuencial a 9 dígitos
          this.invoiceHeader.secuencial = String(data.data.sequence).padStart(9, '0');
          console.log('Secuencial cargado:', this.invoiceHeader.secuencial);
        }
      } catch (error) {
        console.error('Error al cargar secuencial:', error);
        // Si no existe, usar 1 como default
        this.invoiceHeader.secuencial = '000000001';
      }
    },
    async loadAuth() {
      try {
        const { data } = await api.get("/autenticacion/cliente/perfil");
        // data = { user: { id, empresa_id, ... }, modules: [...] }
        this.auth.userId = data?.user?.id ?? null;
        this.auth.companyId = data?.user?.empresa_id ?? null;
        const usuario = data?.user || {};
        this.invoiceHeader.vendedor = usuario.nombres_apellidos || usuario.nombre || usuario.name || usuario.correo_cliente || usuario.correo || 'Usuario logueado';
        console.log('Auth data loaded:', { 
          userId: this.auth.userId, 
          companyId: this.auth.companyId,
          rawData: data?.user 
        });
        
        if (this.auth.companyId === null || this.auth.companyId === undefined) {
          console.error('empresa_id no encontrado en respuesta de /autenticacion/cliente/perfil:', data);
        }
      } catch (e) {
        console.warn("No se pudo obtener /autenticacion/cliente/perfil, verifique sesión.", e);
        console.error('Auth error:', e);
      }
    },
    async cargarModoDescuento() {
      try {
        const resp = await controlAcceso.misPermisos();
        this.modoDescuento = resp?.data?.modo_descuento || 'bloqueado';
      } catch (e) {
        console.warn('No se pudo obtener el modo de descuento del rol.', e);
      }
    },
    async loadEmissionPoints() {
      console.log('Loading emission points...');
      console.log('Current auth state:', { userId: this.auth.userId, companyId: this.auth.companyId });

      // Usar comparación estricta con null porque empresa_id = 0 es válido (super admin)
      if (this.auth.companyId === null || this.auth.companyId === undefined) {
        console.log('No companyId found, loading auth...');
        await this.loadAuth();
      }

      if (this.auth.companyId === null || this.auth.companyId === undefined) {
        console.error('Still no companyId after loading auth');
        this.mostrarNotificacion("No se pudo obtener la información de la compañía.", 'error');
        return;
      }

      console.log('Loading emission points for companyId:', this.auth.companyId);
      this.loadingEmissionPoints = true;
      try {
        console.log('Making API call to /configuracion/puntos-activos');
        const { data } = await api.get("/configuracion/puntos-activos");
        console.log('API Response received:', data);

        if (data.success && data.data) {
          this.emissionPoints = data.data;
          console.log('Emission points loaded:', this.emissionPoints);

          // Seleccionar el punto principal por defecto si existe
          const mainPoint = this.emissionPoints.find(point => point.is_main);
          if (mainPoint) {
            this.invoiceHeader.puntoEmision = mainPoint.emission_point_code;
            console.log('Selected main point:', mainPoint.emission_point_code);
          } else if (this.emissionPoints.length > 0) {
            // Si no hay principal, seleccionar el primero
            this.invoiceHeader.puntoEmision = this.emissionPoints[0].emission_point_code;
            console.log('Selected first point:', this.emissionPoints[0].emission_point_code);
          }
        } else {
          console.warn('No emission points found in response');
          this.mostrarNotificacion("No se encontraron puntos de emisión activos.", 'warning');
        }
      } catch (error) {
        console.error("Error al cargar puntos de emisión:", error);
        console.error("Error details:", {
          message: error.message,
          status: error.response?.status,
          statusText: error.response?.statusText,
          data: error.response?.data
        });
        this.mostrarNotificacion("Error al cargar puntos de emisión.", 'error');
      } finally {
        this.loadingEmissionPoints = false;
      }
    },
    async loadSucursales() {
      this.loadingSucursales = true;
      try {
        const { data } = await api.get('/configuracion/sucursales');
        if (data.success && data.data) {
          this.sucursales = data.data;
          
          // Actualizar el valor seleccionado si la sucursal actual ya no existe o cambió
          this.updateSelectedSucursal();
        } else {
          this.sucursales = [];
        }
      } catch (error) {
        console.error('Error al cargar sucursales:', error);
        this.sucursales = [];
        this.mostrarNotificacion('Error al cargar sucursales', 'error');
      } finally {
        this.loadingSucursales = false;
      }
    },
    updateSelectedSucursal() {
      // Si no hay sucursales, limpiar selección
      if (this.sucursales.length === 0) {
        this.invoiceHeader.sucursal = '';
        return;
      }
      
      // Si la sucursal actualmente seleccionada ya no existe, seleccionar la principal
      const sucursalActual = this.sucursales.find(s => s.nombre === this.invoiceHeader.sucursal);
      if (!sucursalActual) {
        const principal = this.sucursales.find(s => s.es_principal);
        const nuevaSucursal = principal ? principal.nombre : this.sucursales[0].nombre;
        this.invoiceHeader.sucursal = nuevaSucursal;
        console.log('Sucursal actualizada automáticamente a:', nuevaSucursal);
      }
    },
    async reloadSucursales() {
      
      await this.loadSucursales();
      
      // Ya no mostrar mensaje de cambio de sucursal principal
    },
    // Método público para recargar sucursales desde otros componentes
    refreshSucursales() {
      return this.reloadSucursales();
    },
    async loadVendedores() {
      this.loadingVendedores = true;
      try {
        const { data } = await api.get('/configuracion/vendedores');
        if (data.success && data.data) {
          this.vendedores = data.data;
          // Pre-seleccionar el vendedor que corresponde al usuario actual
          const yo = this.vendedores.find(v => v.id === this.auth.userId);
          if (yo) {
            this.invoiceHeader.vendedor = yo.nombre;
          }
        } else {
          this.vendedores = [];
        }
      } catch (error) {
        console.error('Error al cargar vendedores:', error);
        this.vendedores = [];
        this.mostrarNotificacion('Error al cargar vendedores', 'error');
      } finally {
        this.loadingVendedores = false;
      }
    },
    async loadAlmacenes() {
      this.loadingAlmacenes = true;
      try {
        const { data } = await api.get('/configuracion/almacenes');
        if (data.success && data.data) {
          this.almacenes = data.data;
          // Auto-seleccionar la bodega principal
          const principal = this.almacenes.find(
            a => a.principal === 1 || a.principal === true || a.principal === '1'
          );
          if (principal) {
            this.invoiceHeader.bodega = principal.nombre;
          }
        } else {
          this.almacenes = [];
        }
      } catch (error) {
        console.error('Error al cargar almacenes:', error);
        this.almacenes = [];
        this.mostrarNotificacion('Error al cargar almacenes', 'error');
      } finally {
        this.loadingAlmacenes = false;
      }
    },
    async loadAmbienteSri() {
      try {
        const { data } = await api.get('/configuracion/ambiente-sri');
        if (data.success && data.data) {
          this.invoiceHeader.ambiente = data.data.nombre;
        }
      } catch (error) {
        console.error('Error al cargar ambiente SRI:', error);
        // Mantener valor por defecto 'Pruebas'
      }
    },
    mapTipoIdentToCode(tipo) {
      // Si ya es un código numérico ("04", "05", etc.) lo devuelve directo
      const codigosValidos = ['04', '05', '06', '07', '08'];
      if (codigosValidos.includes(String(tipo))) return String(tipo);
      // Compatibilidad con texto legible
      const t = (tipo || "").toLowerCase();
      if (t.includes("ruc")) return "04";
      if (t.includes("cédula") || t.includes("cedula")) return "05";
      if (t.includes("pasaporte")) return "06";
      if (t.includes("consumidor")) return "07";
      return tipo || "05";
    },

    getCurrentEcuadorDate() {
      return fechaIsoSistema();
    },
    mapCodeToTipo(code) {
      switch (String(code)) {
        case "04":
          return "RUC";
        case "05":
          return "Cédula";
        case "06":
          return "Pasaporte";
        case "07":
          return "Consumidor Final";
        default:
          return "Cédula";
      }
    },
    mapFormaPagoToCode(label) {
      // Buscar el código correspondiente en sriPaymentCodes
      for (const [code, description] of Object.entries(this.sriPaymentCodes)) {
        if (description === label) {
          return String(code).padStart(2, '0');
        }
      }
      // Fallback por patrones
      const l = (label || "").toLowerCase();
      if (l.includes("sin utilizacion")) return "01";
      if (l.includes("compensación")) return "15";
      if (l.includes("débito")) return "16";
      if (l.includes("electrónico")) return "17";
      if (l.includes("prepago")) return "18";
      if (l.includes("crédito")) return "19";
      if (l.includes("otros con")) return "20";
      if (l.includes("endoso")) return "21";
      return "20"; // default seguro
    },
    pad(n, width) {
      const s = String(n || "");
      const w = Number(width) || 9;
      return s.length >= w ? s : "0".repeat(w - s.length) + s;
    },
    clearBuyerDataIfChanged() {
      // Solo borrar datos si la identificación ha cambiado y no está vacía
      if (
        this.buyer.identificacion &&
        this.originalBuyerData &&
        this.buyer.identificacion !== this.originalBuyerData.identificacion
      ) {
        this.resetBuyerDataExceptIdentificacion();
        this.originalBuyerData = null;
        this.buyer.clientId = null;
      }
    },
    resetBuyerDataExceptIdentificacion() {
      this.buyer.razonSocial = "";
      this.buyer.telefono = "";
      this.buyer.celular = "";
      this.buyer.correo = "";
      this.buyer.direccion = "";
      // Mantener el tipo de identificación actual
    },
    async buscarClienteOEnSRI() {
      this.loadingSRI = true;
      const identificacion = this.buyer.identificacion?.trim();
      // mapear el tipo legible al código SRI (ej: "RUC" -> "04")
      const tipoCodigo = this.mapTipoIdentToCode(this.buyer.tipoIdentificacion);

      // ? Validaciones previas
      if (!identificacion) {
        this.mostrarNotificacion("Ingrese un número de identificación.", 'error');
        this.loadingSRI = false;
        return;
      }

      if (tipoCodigo === "04" && identificacion.length !== 13) {
        this.mostrarNotificacion("El RUC debe tener 13 dígitos.", 'error');
        this.loadingSRI = false;
        return;
      }

      if (tipoCodigo === "05" && identificacion.length !== 10) {
        this.mostrarNotificacion("La cédula debe tener 10 dígitos.", 'error');
        this.loadingSRI = false;
        return;
      }

      try {
        // 1. Buscar en clientes usando el mismo formato de params que en otras búsquedas (api axios)
        const respClientes = await api.get("/clientes", {
          params: {
            filtros: {
              identification_number: identificacion,
              // usar el campo que el backend espera; aquí usamos *_code para ser consistente con ensureClientExists
              identification_type_sri: tipoCodigo,
            },
            limit: 1,
            offset: 0,
          },
        });

        const inner = respClientes.data || {};
        const list = inner.data?.data || [];

        if (list.length) {
          const c = list[0];
          this.buyer = {
            ...this.buyer,
            clientId: c.id,
            razonSocial: c.razon_social || c.business_name || "",
            direccion: c.direccion || c.address || "",
            telefono: c.telefono || c.phone || "",
            correo: c.correo || c.email || "",
          };
          this.clienteModificadoEnFactura = false;
          this.mostrarNotificacion("Cliente encontrado en tu base de clientes.", 'success');
          this.loadingSRI = false;
          return;
        }

        // 2. Buscar en SRI (solo si es RUC)
        if (tipoCodigo === "04") {
          const sriResp = await api.get(`/sri/ruc-catastro/${identificacion}`);
          const data = sriResp.data || {};
          if (data?.razon_social) {
            this.buyer = {
              ...this.buyer,
              razonSocial: data.razon_social,
              direccion: data.direccion || "",
              telefono: "",
              correo: "",
            };
            this.mostrarNotificacion(
              "Cliente encontrado en SRI. Complete los datos faltantes y guarde como nuevo cliente.", 'success'
            );
            this.loadingSRI = false;
            return;
          }
        }

        // ? No encontrado en clientes ni en SRI
        this.buyer = {
          ...this.buyer,
          razonSocial: "",
          direccion: "",
          telefono: "",
          correo: "",
        };
        this.mostrarNotificacion(
          "Cliente no encontrado. Complete los datos manualmente para registrar un nuevo cliente.", 'warning'
        );
      } catch (error) {
        console.error("Error al buscar cliente:", error);
        this.mostrarNotificacion("No se pudo conectar al servidor.", 'error');
      } finally {
        this.loadingSRI = false;
      }
    },

    onProductSearchInput() {
      clearTimeout(this.productSearchTimeout);
      this.productSearchTimeout = setTimeout(() => {
        const query = (this.productSearchQuery || "").trim();
        if (query.length >= 2) {
          this.buscarProducto();
        } else {
          this.productSearchError = "";
          this.productSuggestions = [];
        }
      }, 500);
    },

    async buscarProducto() {
      // El lector suele enviar Enter. Cancelar la busqueda programada por
      // @input evita procesar el mismo escaneo una segunda vez 500 ms despues.
      clearTimeout(this.productSearchTimeout);
      this.productSearchTimeout = null;

      const query = (this.productSearchQuery || "").trim();
      if (!query) {
        this.productSearchError = "Por favor, ingrese un código o nombre de producto.";
        return;
      }
      this.loadingProductSearch = true;
      this.productSearchError = "";
      try {
        // Una sola llamada usando el filtro unificado 'busqueda'
        // que ya incluye nombre, código principal y código auxiliar
        const res   = await api.get("/productos", {
          params: { filtros: JSON.stringify({ busqueda: query }), limit: 15, offset: 0 },
        });
        const list  = res.data?.data?.productos || res.data?.productos || (Array.isArray(res.data?.data) ? res.data.data : []);

        if (list.length) {
          // Coincidencia exacta por código: auto-agregar sin mostrar lista
          const queryUpper = query.toUpperCase();
          const exacto = list.find(
            p =>
              (p.codigo_principal || "").toUpperCase() === queryUpper ||
              (p.codigo_auxiliar  || "").toUpperCase() === queryUpper
          );
          if (exacto) {
            await this.selectProduct(exacto, false);
            return;
          }
          this.productSuggestions = list;
          this.productSearchError = "";
        } else {
          this.productSuggestions = [];
          this.productSearchError = "Producto no encontrado. Verifique el código o nombre.";
        }
      } catch (e) {
        console.error("Error al buscar producto:", e);
        this.productSearchError = "Error al buscar producto. Verifique conexión o sesión.";
        this.productSuggestions = [];
      } finally {
        this.loadingProductSearch = false;
      }
    },
    calcularLineaProducto(product) {
      if (product.cantidad < 0) product.cantidad = 0;
      if (product.precioUnitario < 0) product.precioUnitario = 0;
      if (product.descuento < 0) product.descuento = 0;
      if (product.descuento > 100) product.descuento = 100;
      if (this.modoDescuento === 'bloqueado') product.descuento = 0;

      const subtotalBase =
        (Number(product.cantidad) || 0) * (Number(product.precioUnitario) || 0);
      const descuentoTotal =
        subtotalBase * ((Number(product.descuento) || 0) / 100);
      const subtotalAplicado = subtotalBase - descuentoTotal;

      // IVA se calcula directo desde porcentajeIVA guardado en el producto
      const pctIVA = Number(product.porcentajeIVA) || 0;
      const iva = pctIVA > 0 ? subtotalAplicado * (pctIVA / 100) : 0;

      product.subtotalSinImpuestos = subtotalAplicado;
      product.descuentoTotal = descuentoTotal;
      product.ice = 0;
      product.iva = iva;
      product.total = Number((subtotalAplicado + iva).toFixed(2));
    },
    buscarProductoAgregado(productoId) {
      const clave = String(productoId);
      return this.productsInInvoice.find(item => String(item.id) === clave);
    },
    incrementarCantidadProducto(producto) {
      const nuevaCantidad = Number(producto.cantidad || 0) + 1;
      if (producto.tipoProducto !== "servicio" && nuevaCantidad > producto.stockDisponible) {
        this.productSearchError = `Stock insuficiente para "${producto.nombre}". Disponible: ${producto.stockDisponible}`;
        return false;
      }

      producto.cantidad = nuevaCantidad;
      this.calcularLineaProducto(producto);
      return true;
    },
    limpiarBusquedaProducto(enfocarInput = true) {
      this.productSearchQuery = "";
      this.productSuggestions = [];
      this.productSearchError = "";
      if (enfocarInput) {
        this.$nextTick(() => this.$refs.inputBusquedaProducto?.focus());
      }
    },
    async selectProduct(p, enfocarInput = true) {
      const claveProducto = String(p.id);

      // Si otra seleccion del mismo producto esta cargando precios, esperar
      // su insercion. Asi dos escaneos rapidos nunca crean lineas paralelas.
      const cargaEnProceso = this.cargasProductoEnProceso.get(claveProducto);
      if (cargaEnProceso) {
        await cargaEnProceso;
        const productoAgregado = this.buscarProductoAgregado(p.id);
        if (productoAgregado && this.incrementarCantidadProducto(productoAgregado)) {
          this.limpiarBusquedaProducto(enfocarInput);
        }
        return;
      }

      try {
        const existente = this.buscarProductoAgregado(p.id);
        if (existente) {
          if (this.incrementarCantidadProducto(existente)) {
            this.limpiarBusquedaProducto(enfocarInput);
          }
          return;
        }

        let finalizarCarga;
        const promesaCarga = new Promise(resolve => {
          finalizarCarga = resolve;
        });
        this.cargasProductoEnProceso.set(claveProducto, promesaCarga);

        // Cargar precios del producto
        try {
          const preciosResponse = await api.get(`/productos/${p.id}/precios`);
          const precios = preciosResponse.data?.data || [];

          // Encontrar Precio1 como precio por defecto; si no hay, usar precio_unitario del producto
          const precio1 = precios.find(pr => pr.tipo_precio === 'Precio1');
          const precioDefault = precio1 ? parseFloat(precio1.precio) : parseFloat(p.precio_unitario || 0);

          // Determinar % IVA desde tarifa_impuesto (0, 5, 15 o negativo para exento/no objeto)
          const tarifa = parseFloat(p.tarifa_impuesto || 0);
          const porcentajeIVA = tarifa > 0 ? tarifa : 0;
          const aplicaIVA = tarifa > 0;

          const newProduct = {
            id: p.id,
            codigo: p.codigo_principal || "",
            nombre: p.nombre_producto || "",
            tipoProducto: p.tipo_producto || "bien",
            stockDisponible: p.tipo_producto === "servicio" ? Infinity : parseFloat(p.stock_total || 0),
            cantidad: 1,
            precioUnitario: precioDefault,
            preciosDisponibles: precios,
            precioSeleccionado: precio1 ? precio1.tipo_precio : null,
            permitePrecioManual: p.permite_precio_manual == 1,
            permiteNombreManual: p.permite_nombre_manual == 1,
            descuento: parseFloat(p.descuento || 0),
            porcentajeIVA,
            taxCode: tarifa >= 15 ? '2' : tarifa === 5 ? '3' : tarifa === 0 && aplicaIVA ? '0' : aplicaIVA ? '6' : '7',
            porcentajeICE: 0,
            aplicaIVA,
            subtotalSinImpuestos: 0,
            descuentoTotal: 0,
            ice: 0,
            iva: 0,
            total: 0,
          };

          this.calcularLineaProducto(newProduct);
          this.productsInInvoice.push(newProduct);

          // Limpiar busqueda y re-enfocar el campo para el siguiente codigo
          this.limpiarBusquedaProducto(enfocarInput);
        } finally {
          this.cargasProductoEnProceso.delete(claveProducto);
          finalizarCarga();
        }
      } catch (error) {
        console.error("Error al cargar precios del producto:", error);
        this.mostrarNotificacion("Error al cargar precios del producto", 'error');
      }
    },
    cambiarPrecioProducto(product) {
      if (product.precioSeleccionado === '__manual__') {
        this.calcularLineaProducto(product);
        return;
      }
      const precioSeleccionado = product.preciosDisponibles.find(p => p.tipo_precio === product.precioSeleccionado);
      if (precioSeleccionado) {
        product.precioUnitario = parseFloat(precioSeleccionado.precio);
        this.calcularLineaProducto(product);
      }
    },
    async eliminarProducto(index) {
      const confirmado = await this.mostrarConfirmacion(
        'Eliminar producto',
        '¿Está seguro de que desea eliminar este producto?',
        'Eliminar',
        'Cancelar'
      );

      if (confirmado) {
        this.productsInInvoice.splice(index, 1);
      }
    },
    addFormaPago() {
      if (this.invoiceFooter.valorPagar <= 0) {
        this.mostrarNotificacion("El valor a pagar debe ser mayor a 0.", 'error');
        return;
      }
      this.invoiceFooter.formasPagoAgregadas.push({
        formaPago: this.invoiceFooter.formaPago,
        valorPagar: this.invoiceFooter.valorPagar,
        pagoPlazo: this.invoiceFooter.pagoPlazo,
        plazoDias: this.invoiceFooter.pagoPlazo
          ? this.invoiceFooter.plazoDias
          : 0,
      });
    },
    async removeFormaPago(index) {
      const confirmado = await this.mostrarConfirmacion(
        'Eliminar forma de pago',
        '¿Está seguro de que desea eliminar esta forma de pago?',
        'Eliminar',
        'Cancelar'
      );

      if (confirmado) {
        this.invoiceFooter.formasPagoAgregadas.splice(index, 1);
      }
    },
    async ensureClientExists() {
      if (this.auth.companyId === null || this.auth.companyId === undefined) {
        await this.loadAuth();
      }
      const payload = {
        empresa_id: this.auth.companyId,
        tipo_identificacion: this.buyer.tipoIdentificacion,
        identification_number: this.buyer.identificacion,
        business_name: this.buyer.razonSocial,
        address: this.buyer.direccion || null,
        phone: this.buyer.telefono || null,
        email: this.buyer.correo || null,
      };

      // Los datos del comprador son editables desde Facturacion. Si el
      // cliente ya existe, persistirlos antes de guardar la factura.
      if (this.buyer.clientId && this.clienteModificadoEnFactura) {
        await api.put(`/clientes/${this.buyer.clientId}`, payload);
        this.clienteModificadoEnFactura = false;
        return this.buyer.clientId;
      }
      if (this.buyer.clientId) {
        return this.buyer.clientId;
      }

      try {
        const { data } = await api.post('/clientes', payload);
        const newId = data?.id || data?.cliente_id || null;
        if (!newId) throw new Error('El backend no retornó el ID del cliente creado.');
        this.buyer.clientId = newId;
        return newId;
      } catch (err) {
        // 409 = cliente ya existe; el backend devuelve el id del registro existente
        const existingId = err.response?.data?.id || null;
        if (err.response?.status === 409 && existingId) {
          this.buyer.clientId = existingId;
          return existingId;
        }
        throw err;
      }
    },
    async guardarFactura(firmarYEnviar) {
      const erroresValidacion = await this.validarFacturaAntesGuardar();
      if (erroresValidacion.length > 0) {
        this.mostrarNotificacion(erroresValidacion.join('\n'), 'error');
        return;
      }

      // Bloquear botones para evitar doble envío
      this.procesandoFactura = true;
      this.mostrarNotificacion('Guardando factura...', 'info');

      try {
        if (!this.auth.userId || (this.auth.companyId === null || this.auth.companyId === undefined)) {
          await this.loadAuth();
        }
        
        // Validar que tenemos empresa_id después de cargar auth
        if (this.auth.companyId === null || this.auth.companyId === undefined) {
          this.mostrarNotificacion('No se pudo obtener el ID de empresa. Por favor, cierre sesión e inicie sesión nuevamente.', 'error');
          console.error('empresa_id faltante después de loadAuth. Auth state:', this.auth);
          return;
        }
        
        const clientId = await this.ensureClientExists();

        const selectedPoint = this.emissionPoints.find(point => point.emission_point_code === this.invoiceHeader.puntoEmision);
        const establishmentCode = selectedPoint ? selectedPoint.establishment_code : '001';

        const payload = {
          intent: firmarYEnviar ? "ISSUE_INVOICE" : "SAVE_DRAFT",
          empresa_id: this.auth.companyId,
          client_id: clientId,
          client_user_id: this.auth.userId,
          establecimiento: establishmentCode,
          punto_emision: this.invoiceHeader.puntoEmision,
          cliente: {
            identificacion: this.buyer.identificacion,
            razon_social: this.buyer.razonSocial,
            direccion: this.buyer.direccion,
            correo: this.buyer.correo,
            telefono: this.buyer.telefono,
            tipo_identificacion: this.buyer.tipoIdentificacion
          },
          productos: this.productsInInvoice.map((it) => ({
            product_id: it.id || null,
            codigo: it.codigo,
            descripcion: it.nombre,
            cantidad: Number(it.cantidad),
            precioUnitario: Number(it.precioUnitario),
            descuento: Number(it.descuento || 0),
            aplicaIVA: it.aplicaIVA
          })),
          forma_pago: this.mapFormaPagoToCode(this.invoiceFooter.formaPago),
          notas: this.invoiceFooter.descripcion || null,
          es_credito_interno: this.invoiceHeader.esCreditoInterno ? 1 : 0,
          dias_credito: this.invoiceHeader.esCreditoInterno ? (this.invoiceHeader.diasCredito || 30) : null,
        };

        // Si la factura viene de una orden de taller, el backend omite el
        // descargo de inventario (los repuestos ya se descontaron al
        // cargarlos a la orden).
        if (this.ordenTallerOrigen && this.ordenTallerOrigen.orden_taller_id) {
          payload.orden_taller_id = this.ordenTallerOrigen.orden_taller_id;
        }

        // Si estamos editando un borrador existente, usar PUT; si no, crear nuevo
        const { data } = this.facturaEditandoId
          ? await api.put(`/facturas/${this.facturaEditandoId}`, payload)
          : await api.post('/facturas/crear', payload)

        const ok = data?.success === true || !!data?.factura_id || !!data?.facturaId;
        if (ok) {
          const facturaId = data.factura_id ?? data.facturaId ?? this.facturaEditandoId;
          
          // -- Solo guardar -------------------------------------------------
          if (!firmarYEnviar) {
            if (this.facturaEditandoId) {
              this.mostrarNotificacion(
                `Borrador actualizado correctamente. ID: ${facturaId ?? 'N/A'}`,
                'success'
              );
              this.facturaEditandoId = null;
              this.$router.push('/ventas/documentos-emitidos');
            } else {
              this.mostrarNotificacion(
                'Factura guardada correctamente. Formulario listo para nueva factura.',
                'success'
              );
              // Limpiar y recargar secuencial para nueva factura
              this.limpiarFormulario();
              await this.loadSequenceForEmissionPoint(this.invoiceHeader.puntoEmision);
            }
            return;
          }

          // -- Guardar, Firmar y Enviar --------------------------------------
          this.mostrarNotificacion('? Guardada. Enviando al SRI en segundo plano...', 'info');

          // Lanzar firma al SRI SIN BLOQUEAR — el modal de cobro aparece de inmediato
          const promesaFirma = this.enviarAlSriEnBackground(facturaId);

          if (this.invoiceHeader.esCreditoInterno) {
            // Factura a crédito: no se cobra en este momento, imprimir ticket directo.
            await this.imprimirTicketDirectoPostFactura(facturaId, promesaFirma);
          } else if (this.pedidoOrigen && this.pedidoOrigen.ya_pagado) {
            // Pedido ya cobrado: saltar modal de cobro, imprimir ticket directo.
            await this.imprimirTicketDirectoPostFactura(facturaId, promesaFirma);
          } else if (this.isPlanBasico) {
            // Plan basico no usa caja, omitir modal de cobro.
            await this.imprimirTicketDirectoPostFactura(facturaId, promesaFirma);
          } else {
            // Factura de contado: mostrar cobro inmediato.
            await this.abrirModalCobroPostFactura(facturaId, promesaFirma);
          }

          // Limpiar formulario y recargar secuencial para nueva factura
          this.limpiarFormulario();
          await this.loadSequenceForEmissionPoint(this.invoiceHeader.puntoEmision);

        } else {
          // Mostrar errores específicos del backend (validaciones SRI, cálculos, etc.)
          const mensajeError = data?.message || data?.error || "El backend no confirmó la creación de la factura.";
          throw new Error(mensajeError);
        }
      } catch (e) {
        console.error("Error al guardar/enviar factura:", e);
        console.error("Response data:", e.response?.data);
        console.error("Response status:", e.response?.status);
        
        let mensajeUsuario = "Error al guardar/enviar factura: ";
        if (e.response?.data?.message) {
          mensajeUsuario += e.response.data.message;
        } else if (e.response?.data?.error) {
          mensajeUsuario += e.response.data.error;
        } else {
          mensajeUsuario += e.message;
        }
        
        this.mostrarNotificacion(mensajeUsuario, 'error');
      } finally {
        // Siempre desbloquear los botones al terminar
        this.procesandoFactura = false;
      }
    },
    async nuevaFactura() {
      // Verificar si hay datos en la factura actual
      const tieneDatos = this.buyer.identificacion ||
                        this.buyer.razonSocial ||
                        this.productsInInvoice.length > 0 ||
                        this.invoiceFooter.descripcion;

      if (tieneDatos) {
        // Mostrar diálogo de confirmación
        const guardarBorrador = await this.mostrarConfirmacion(
          'Guardar como borrador',
          '¿Desea guardar la factura actual como borrador antes de crear una nueva?',
          'Guardar y crear nueva',
          'No guardar'
        );

        if (guardarBorrador) {
          // Guardar como borrador en la base de datos
          const guardado = await this.guardarBorrador();
          if (guardado) {
            this.mostrarNotificacion("Factura guardada como borrador. Puede recuperarla más tarde.", 'info');
          } else {
            // Si falló el guardado, preguntar si quiere continuar
            const continuar = await this.mostrarConfirmacion(
              'Error al guardar borrador',
              'No se pudo guardar el borrador. ¿Desea continuar creando una nueva factura?',
              'Continuar',
              'Cancelar'
            );
            if (!continuar) return;
          }
        } else {
          // Preguntar si está seguro de perder los datos
          const descartar = await this.mostrarConfirmacion(
            'Descartar factura actual',
            '¿Está seguro de que desea descartar la factura actual y crear una nueva? Se perderán todos los datos no guardados.',
            'Descartar y crear nueva',
            'Cancelar'
          );

          if (!descartar) {
            return; // Cancelar la operación
          }
        }
      }

      // Limpiar el formulario para nueva factura
      this.limpiarFormulario();
      this.mostrarNotificacion("Nueva factura iniciada.", 'success');
    },
    async guardarBorrador(draftName = null, draftDescription = null) {
      try {
        // Si no se pasaron nombre y descripción, mostrar modal para pedirlos
        if (!draftName || !draftDescription) {
          const result = await this.mostrarModalGuardarBorrador();
          if (!result) return false;

          draftName = result.name;
          draftDescription = result.description;
        }

        // Validar borrador antes de guardar
        const erroresValidacion = await this.validarBorradorAntesGuardar(draftName, draftDescription);
        if (erroresValidacion.length > 0) {
          this.mostrarNotificacion(erroresValidacion.join('\n'), 'error');
          return false;
        }

        const payload = {
          draft_name: draftName,
          draft_description: draftDescription,
          buyer_data: this.buyer,
          products_data: this.productsInInvoice,
          invoice_footer_data: this.invoiceFooter,
          invoice_header_data: this.invoiceHeader,
          total_amount: this.valorTotal
        };

        const { data } = await api.post('/facturas-borrador', payload);

        if (data.success) {
          this.mostrarNotificacion('Borrador guardado exitosamente', 'success');
          // Recargar la lista de borradores
          await this.cargarListaBorradores();
          return true;
        } else {
          throw new Error(data.error || 'Error al guardar borrador');
        }
      } catch (error) {
        console.error('Error al guardar borrador:', error);
        this.mostrarNotificacion(`Error al guardar borrador: ${error.message}`, 'error');
        return false;
      }
    },
    // ── Avisos del superadmin (banner descartable en Facturacion) ──────────
    // El descarte es solo para la carga de pagina actual: al refrescar o
    // volver a entrar, las notificaciones vigentes se muestran de nuevo.
    async cargarNotificacionesActivas() {
      try {
        const { data } = await api.get('/notificaciones/activas')
        this.notificacionesActivas = data?.data || []
      } catch (_e) {
        // Silencioso: un aviso que no carga no debe interrumpir la facturacion
      }
    },
    descartarNotificacion(id) {
      this.notificacionesActivas = this.notificacionesActivas.filter(n => n.id !== id)
    },
    etiquetaTipo(tipo) {
      const mapa = { info: 'Informativo', advertencia: 'Advertencia', urgente: 'Urgente' }
      return mapa[tipo] || mapa.info
    },
    iconoAvisoSistema(tipo) {
      const mapa = {
        info:        'bi bi-megaphone-fill',
        advertencia: 'bi bi-exclamation-triangle-fill',
        urgente:     'bi bi-exclamation-octagon-fill',
      }
      return mapa[tipo] || mapa.info
    },

    async cargarFacturaParaEditar(id) {
      try {
        this.mostrarNotificacion(
          this.soloLectura ? 'Cargando factura...' : 'Cargando factura para editar...',
          'info'
        )
        const { data } = await api.get(`/facturas/${id}`)
        const f = data

        if (!f || !f.id) {
          this.mostrarNotificacion('No se encontró la factura', 'error')
          return
        }

        this.facturaEditandoId = f.id

        // Restaurar secuencial original de la factura (no generar uno nuevo)
        if (f.secuencial) {
          this.invoiceHeader.secuencial = String(f.secuencial).padStart(9, '0')
        }

        // Cargar cliente
        this.buyer = {
          clientId:          f.cliente_id ?? null,
          identificacion:    f.cliente_identificacion ?? '',
          // TINYINT en BD (4,5,6,7) ? string con cero a la izquierda ('04','05','06','07')
          tipoIdentificacion: f.cliente_tipo_identificacion != null
            ? String(f.cliente_tipo_identificacion).padStart(2, '0')
            : '',
          razonSocial:       f.cliente_razon_social ?? '',
          correo:            f.cliente_email ?? '',
          telefono:          f.cliente_telefono ?? '',
          direccion:         f.cliente_direccion ?? '',
          celular:           '',
        }

        // Cargar productos con precios disponibles y recalcular campos derivados
        this.productsInInvoice = await Promise.all((f.detalles ?? []).map(async d => {
          const precioUnitario = Number(d.precio_unitario)
          const cantidad       = Number(d.cantidad)
          // La BD guarda descuento como valor monetario; el frontend usa porcentaje (0-100)
          const descuentoVal   = Number(d.descuento ?? 0)
          const bruto          = precioUnitario * cantidad
          const descuentoPct   = (bruto > 0 && descuentoVal > 0)
            ? Math.round((descuentoVal / bruto) * 100 * 10000) / 10000
            : 0
          const porcentajeIVA  = Number(d.porcentaje_iva ?? 15)
          // Derivar taxCode desde porcentaje_iva (15?'2', 5?'3', 0?'0')
          const taxCode = porcentajeIVA === 15 ? '2' : porcentajeIVA === 5 ? '3' : '0'

          // Cargar lista de precios del producto para que el select funcione
          let preciosDisponibles = []
          let precioSeleccionado = null
          if (d.producto_id) {
            try {
              const resp = await api.get(`/productos/${d.producto_id}/precios`)
              preciosDisponibles = resp.data?.data || []
              const match = preciosDisponibles.find(
                pr => Math.abs(parseFloat(pr.precio) - precioUnitario) < 0.001
              )
              precioSeleccionado = match?.id ?? (preciosDisponibles[0]?.id ?? null)
            } catch (_e) {
              // Si no se pueden cargar los precios, continuar sin ellos
            }
          }

          const p = {
            id:                    d.producto_id ?? null,
            codigo:                d.codigo_principal ?? '',
            nombre:                d.descripcion ?? '',
            tipoProducto:          d.producto_tipo_producto || 'bien',
            stockDisponible:       d.producto_tipo_producto === 'servicio' ? Infinity : parseFloat(d.producto_stock_disponible || 0),
            cantidad,
            precioUnitario,
            preciosDisponibles,
            precioSeleccionado,
            descuento:             descuentoPct,
            porcentajeIVA,
            aplicaIVA:             porcentajeIVA > 0,
            subtotalSinImpuestos:  0,
            iva:                   0,
            ice:                   0,
            total:                 0,
            descuentoTotal:        0,
            taxCode,
            porcentajeICE:         0,
          }
          this.calcularLineaProducto(p)
          return p
        }))

        // Notas / forma de pago
        this.invoiceFooter.descripcion = f.notas ?? ''
        const primerPago = (f.pagos ?? [])[0]
        if (primerPago) {
          // Usar sriPaymentCodes para obtener el valor exacto que usa el <select>
          const codigoFormaPago = String(primerPago.forma_pago ?? '').padStart(2, '0')
          const descripcion = this.sriPaymentCodes[codigoFormaPago]
          if (descripcion) {
            this.invoiceFooter.formaPago = descripcion
          }
        }

        this.mostrarNotificacion(
          this.soloLectura ? 'Factura cargada (solo lectura)' : 'Factura cargada para editar',
          'success'
        )
      } catch (error) {
        console.error('Error al cargar factura para editar:', error)
        this.mostrarNotificacion('Error al cargar la factura: ' + (error.message || ''), 'error')
        this.facturaEditandoId = null
      }
    },

    // Pre-carga el formulario de factura desde los datos de una proforma convertida.
    // Los datos vienen del sessionStorage (puestos por ClientVentasProformar).
    async cargarDesdeProforma(pf) {
      try {
        this.mostrarNotificacion('Cargando datos de la proforma ' + (pf.numero_proforma || '') + '...', 'info')

        // Mapear tipo_identificacion (número entero ? string con cero inicial)
        const tipoId = pf.tipo_identificacion_cliente != null
          ? String(pf.tipo_identificacion_cliente).padStart(2, '0')
          : ''

        this.buyer = {
          clientId:            pf.cliente_id ?? null,
          identificacion:      pf.identificacion_cliente ?? '',
          tipoIdentificacion:  tipoId,
          razonSocial:         pf.nombre_cliente ?? '',
          correo:              pf.correo_cliente ?? '',
          telefono:            pf.telefono_cliente ?? '',
          celular:             '',
          direccion:           pf.direccion_cliente ?? '',
        }

        // Cargar productos con precios disponibles
        this.productsInInvoice = await Promise.all((pf.productos || []).map(async d => {
          const precioUnitario = Number(d.precio_unitario ?? 0)
          const cantidad       = Number(d.cantidad ?? 1)
          const descuentoPct   = Number(d.descuento ?? 0)
          const porcentajeIVA  = Number(d.porcentaje_iva ?? 15)
          const taxCode        = porcentajeIVA === 15 ? '2' : porcentajeIVA === 5 ? '3' : '0'

          let preciosDisponibles = []
          let precioSeleccionado = null
          if (d.producto_id) {
            try {
              const resp = await api.get('/productos/' + d.producto_id + '/precios')
              preciosDisponibles = resp.data?.data || []
              const match = preciosDisponibles.find(
                pr => Math.abs(parseFloat(pr.precio) - precioUnitario) < 0.001
              )
              precioSeleccionado = match?.id ?? (preciosDisponibles[0]?.id ?? null)
            } catch (_e) { /* continuar sin precios */ }
          }

          const p = {
            id:                   d.producto_id ?? null,
            codigo:               d.codigo ?? '',
            nombre:               d.nombre ?? '',
            tipoProducto:         d.producto_tipo_producto || 'bien',
            stockDisponible:      d.producto_tipo_producto === 'servicio' ? Infinity : parseFloat(d.producto_stock_disponible || 0),
            cantidad,
            precioUnitario,
            preciosDisponibles,
            precioSeleccionado,
            descuento:            descuentoPct,
            porcentajeIVA,
            aplicaIVA:            porcentajeIVA > 0,
            subtotalSinImpuestos: 0,
            iva:                  0,
            ice:                  0,
            total:                0,
            descuentoTotal:       0,
            taxCode,
            porcentajeICE:        0,
          }
          this.calcularLineaProducto(p)
          return p
        }))

        if (pf.notas) this.invoiceFooter.descripcion = pf.notas

        // Guardar referencia a la proforma para usarla en el modal de cobro
        if (pf.proforma_id) {
          this.proformaOrigen = {
            proforma_id:      pf.proforma_id,
            numero_proforma:  pf.numero_proforma || '',
            total_anticipos:  Number(parseFloat(pf.total_anticipos || 0).toFixed(2)),
            saldo_pendiente:  Number(parseFloat(pf.saldo_pendiente  || 0).toFixed(2)),
          }
        }

        this.mostrarNotificacion('Proforma ' + (pf.numero_proforma || '') + ' cargada. Revisa los datos y emite la factura.', 'success')
      } catch (error) {
        this.mostrarNotificacion('Error al cargar datos de la proforma.', 'error')
      }
    },

    mapCodigoToFormaPago(codigo) {
      const mapa = {
        '01': 'Efectivo',
        '15': 'Compensación de deudas',
        '16': 'Tarjeta de débito',
        '17': 'Dinero electrónico',
        '18': 'Tarjeta prepago',
        '19': 'Tarjeta de crédito',
        '20': 'Otros con Utilización del Sistema Financiero',
        '21': 'Endoso de títulos',
      }
      return mapa[codigo] ?? null
    },

    async cargarDesdePedido(pedido) {
      try {
        this.mostrarNotificacion('Cargando datos del pedido ' + (pedido.numero_pedido || '') + '...', 'info')

        const tipoId = pedido.tipo_identificacion_cliente != null
          ? String(pedido.tipo_identificacion_cliente).padStart(2, '0')
          : ''

        this.buyer = {
          clientId:           pedido.cliente_id ?? null,
          identificacion:     pedido.identificacion_cliente ?? '',
          tipoIdentificacion: tipoId,
          razonSocial:        pedido.nombre_cliente ?? '',
          correo:             pedido.correo_cliente ?? '',
          telefono:           pedido.telefono_cliente ?? '',
          celular:            '',
          direccion:          pedido.direccion_cliente ?? '',
        }

        this.productsInInvoice = await Promise.all((pedido.productos || []).map(async d => {
          const precioUnitario = Number(d.precio_unitario ?? 0)
          const cantidad       = Number(d.cantidad ?? 1)
          const descuentoPct   = Number(d.descuento ?? 0)
          const porcentajeIVA  = Number(d.porcentaje_iva ?? 15)
          const taxCode        = porcentajeIVA === 15 ? '2' : porcentajeIVA === 5 ? '3' : '0'

          let preciosDisponibles = []
          let precioSeleccionado = null
          if (d.producto_id) {
            try {
              const resp = await api.get('/productos/' + d.producto_id + '/precios')
              preciosDisponibles = resp.data?.data || []
              const match = preciosDisponibles.find(
                pr => Math.abs(parseFloat(pr.precio) - precioUnitario) < 0.001
              )
              precioSeleccionado = match?.id ?? (preciosDisponibles[0]?.id ?? null)
            } catch (_e) { /* continuar sin precios */ }
          }

          const p = {
            id:                   d.producto_id ?? null,
            codigo:               d.codigo ?? '',
            nombre:               d.nombre ?? d.descripcion ?? '',
            tipoProducto:         d.producto_tipo_producto || 'bien',
            stockDisponible:      d.producto_tipo_producto === 'servicio' ? Infinity : parseFloat(d.stock_disponible || 0),
            cantidad,
            precioUnitario,
            preciosDisponibles,
            precioSeleccionado,
            descuento:            descuentoPct,
            porcentajeIVA,
            aplicaIVA:            porcentajeIVA > 0,
            subtotalSinImpuestos: 0,
            iva:                  0,
            ice:                  0,
            total:                0,
            descuentoTotal:       0,
            taxCode,
            porcentajeICE:        0,
          }
          this.calcularLineaProducto(p)
          return p
        }))

        if (pedido.notas) this.invoiceFooter.descripcion = pedido.notas

        // Guardar referencia al pedido para confirmar conversión tras emitir
        this.pedidoOrigen = {
          pedido_id:     pedido.pedido_id,
          numero_pedido: pedido.numero_pedido || '',
          ya_pagado:     !!pedido.ya_pagado,
        }

        const aviso = pedido.ya_pagado
          ? 'Pedido ' + (pedido.numero_pedido || '') + ' (PAGADO) cargado. El cobro se saltará al emitir la factura.'
          : 'Pedido ' + (pedido.numero_pedido || '') + ' cargado. Revisa los datos y emite la factura.'
        this.mostrarNotificacion(aviso, 'success')
      } catch (error) {
        this.mostrarNotificacion('Error al cargar datos del pedido.', 'error')
      }
    },

    async cargarDesdeOrdenTaller(orden) {
      try {
        this.mostrarNotificacion('Cargando datos de la orden ' + (orden.numero_orden || '') + '...', 'info')

        const tieneCliente = !!orden.cliente_id
        const tipoId = orden.tipo_identificacion_cliente != null
          ? String(orden.tipo_identificacion_cliente).padStart(2, '0')
          : ''

        // Si la orden no tiene cliente registrado (cliente ocasional), se
        // precargan los datos del propietario como punto de partida, pero
        // el usuario debe elegir un cliente registrado o Consumidor Final
        // antes de emitir (igual que en el resto de Facturacion).
        this.buyer = {
          clientId:           orden.cliente_id ?? null,
          identificacion:     tieneCliente ? (orden.identificacion_cliente ?? '') : '',
          tipoIdentificacion: tieneCliente ? tipoId : '',
          razonSocial:        tieneCliente ? (orden.nombre_cliente ?? '') : (orden.propietario_nombre ?? ''),
          correo:             orden.correo_cliente ?? '',
          telefono:           tieneCliente ? (orden.telefono_cliente ?? '') : (orden.propietario_telefono ?? ''),
          celular:            '',
          direccion:          orden.direccion_cliente ?? '',
        }

        this.productsInInvoice = await Promise.all((orden.productos || []).map(async d => {
          const precioUnitario = Number(d.precio_unitario ?? 0)
          const cantidad       = Number(d.cantidad ?? 1)
          const descuentoPct   = Number(d.descuento ?? 0)
          const porcentajeIVA  = Number(d.porcentaje_iva ?? 15)
          const taxCode        = porcentajeIVA === 15 ? '2' : porcentajeIVA === 5 ? '3' : '0'

          let preciosDisponibles = []
          let precioSeleccionado = null
          if (d.producto_id) {
            try {
              const resp = await api.get('/productos/' + d.producto_id + '/precios')
              preciosDisponibles = resp.data?.data || []
              const match = preciosDisponibles.find(
                pr => Math.abs(parseFloat(pr.precio) - precioUnitario) < 0.001
              )
              precioSeleccionado = match?.id ?? (preciosDisponibles[0]?.id ?? null)
            } catch (_e) { /* continuar sin precios */ }
          }

          const p = {
            id:                   d.producto_id ?? null,
            codigo:               d.producto_codigo ?? '',
            nombre:               d.producto_nombre ?? '',
            tipoProducto:         d.tipo_producto || 'bien',
            stockDisponible:      d.tipo_producto === 'servicio' ? Infinity : parseFloat(d.stock_disponible || 0),
            cantidad,
            precioUnitario,
            preciosDisponibles,
            precioSeleccionado,
            descuento:            descuentoPct,
            porcentajeIVA,
            aplicaIVA:            porcentajeIVA > 0,
            subtotalSinImpuestos: 0,
            iva:                  0,
            ice:                  0,
            total:                0,
            descuentoTotal:       0,
            taxCode,
            porcentajeICE:        0,
          }
          this.calcularLineaProducto(p)
          return p
        }))

        const notasOrden = [orden.notas, orden.placa ? ('Placa: ' + orden.placa) : null]
          .filter(Boolean).join(' — ')
        if (notasOrden) this.invoiceFooter.descripcion = notasOrden

        // Guardar referencia a la orden para confirmar conversión tras emitir.
        // No se descuenta stock aqui: los repuestos ya se descontaron al
        // cargarlos a la orden (OrdenTallerService).
        this.ordenTallerOrigen = {
          orden_taller_id: orden.orden_taller_id,
          numero_orden:    orden.numero_orden || '',
        }

        this.mostrarNotificacion(
          'Orden ' + (orden.numero_orden || '') + ' cargada. Revisa los datos y emite la factura.',
          'success'
        )
      } catch (error) {
        this.mostrarNotificacion('Error al cargar datos de la orden de taller.', 'error')
      }
    },

    async cargarBorrador(id) {
      try {
        const { data } = await api.get(`/facturas-borrador/${id}`);

        if (data.success) {
          const borrador = data.data;

          // Cargar datos del borrador
          this.buyer = borrador.buyer_data;
          this.productsInInvoice = borrador.products_data;
          this.invoiceFooter = borrador.invoice_footer_data;
          this.invoiceHeader = borrador.invoice_header_data;

          this.mostrarNotificacion('Borrador cargado exitosamente', 'success');

          // Eliminar el borrador cargado del servidor
          await api.delete(`/facturas-borrador/${id}`);
          // Recargar la lista de borradores
          await this.cargarListaBorradores();
        } else {
          throw new Error(data.error || 'Error al cargar borrador');
        }
      } catch (error) {
        console.error('Error al cargar borrador:', error);
        this.mostrarNotificacion(`Error al cargar borrador: ${error.message}`, 'error');
      }
    },
    async limpiarTodosBorradores() {
      const confirmado = await this.mostrarConfirmacion(
        'Eliminar todos los borradores',
        '¿Está seguro de que desea eliminar todos los borradores? Esta acción no se puede deshacer.',
        'Eliminar todos',
        'Cancelar'
      );

      if (confirmado) {
        try {
          // Obtener todos los borradores y eliminarlos uno por uno
          const borradores = this.borradoresDisponibles;
          for (const borrador of borradores) {
            await api.delete(`/facturas-borrador/${borrador.id}`);
          }

          this.mostrarNotificacion("Todos los borradores han sido eliminados.", 'info');
          await this.cargarListaBorradores();
        } catch (error) {
          console.error('Error al eliminar borradores:', error);
          this.mostrarNotificacion(`Error al eliminar borradores: ${error.message}`, 'error');
        }
      }
    },
    async cargarListaBorradores() {
      try {
        const { data } = await api.get('/facturas-borrador');
        if (data.success) {
          this.borradoresDisponiblesData = data.data;
        }
      } catch (error) {
        console.error('Error al cargar lista de borradores:', error);
        this.borradoresDisponiblesData = [];
      }
    },
    mostrarModalGuardarBorrador() {
      return new Promise((resolve) => {
        // Crear modal dinámicamente
        const modalHtml = `
          <div class="modal fade" id="saveDraftModal" tabindex="-1" aria-labelledby="saveDraftModalLabel" aria-hidden="true">
            <div class="modal-dialog modal-dialog-centered">
              <div class="modal-content">
                <div class="modal-header">
                  <h5 class="modal-title" id="saveDraftModalLabel">Guardar Borrador</h5>
                  <button type="button" class="btn-close" data-bs-dismiss="modal" aria-label="Close"></button>
                </div>
                <div class="modal-body">
                  <div class="mb-3">
                    <label for="draftName" class="form-label">Nombre del borrador *</label>
                    <input type="text" class="form-control" id="draftName" placeholder="Ej: Factura para cliente ABC" required>
                  </div>
                  <div class="mb-3">
                    <label for="draftDescription" class="form-label">Descripción *</label>
                    <textarea class="form-control" id="draftDescription" rows="3" placeholder="Detalles adicionales del borrador..." required></textarea>
                  </div>
                </div>
                <div class="modal-footer">
                  <button type="button" class="btn btn-secondary" data-bs-dismiss="modal">Cancelar</button>
                  <button type="button" class="btn btn-primary" id="saveDraftBtn">Guardar Borrador</button>
                </div>
              </div>
            </div>
          </div>
        `;

        // Agregar modal al DOM
        document.body.insertAdjacentHTML('beforeend', modalHtml);

        const modal = new Modal(document.getElementById('saveDraftModal'));
        const saveBtn = document.getElementById('saveDraftBtn');
        const nameInput = document.getElementById('draftName');
        const descInput = document.getElementById('draftDescription');

        saveBtn.addEventListener('click', () => {
          const name = nameInput.value.trim();
          const description = descInput.value.trim();

          if (!name || !description) {
            this.mostrarNotificacion('Nombre y descripción son obligatorios', 'error');
            return;
          }

          modal.hide();
          // Limpiar modal del DOM después de ocultarlo
          setTimeout(() => {
            document.getElementById('saveDraftModal').remove();
          }, 300);

          resolve({ name, description });
        });

        // Resolver con null si se cancela
        document.getElementById('saveDraftModal').addEventListener('hidden.bs.modal', () => {
          setTimeout(() => {
            const modalElement = document.getElementById('saveDraftModal');
            if (modalElement) modalElement.remove();
          }, 300);
          resolve(null);
        });

        modal.show();
      });
    },
    cerrarDropdownBorradores(event) {
      // Cerrar dropdown si se hace clic fuera de él
      const dropdown = event.target.closest('.dropdown');
      if (!dropdown) {
        this.mostrarBorradores = false;
      }
    },
    mostrarConfirmacion(title, message, acceptText = 'Aceptar', cancelText = 'Cancelar') {
      return new Promise((resolve) => {
        this.confirmTitle = title;
        this.confirmMessage = message;
        this.confirmAcceptText = acceptText;
        this.confirmCancelText = cancelText;
        this.confirmCallback = (result) => {
          resolve(result);
          // Cerrar el modal
          const modal = Modal.getInstance(this.$refs.confirmModal);
          if (modal) {
            modal.hide();
          }
        };

        const modal = new Modal(this.$refs.confirmModal);
        modal.show();
      });
    },
    limpiarFormulario() {
      // Reiniciar datos del comprador
      this.buyer = {
        clientId: null,
        identificacion: "",
        tipoIdentificacion: "",
        razonSocial: "",
        telefono: "",
        celular: "",
        correo: "",
        direccion: "",
      };
      this.clienteModificadoEnFactura = false;

      // Reiniciar producto temporal
      this.currentProduct = {
        id: null,
        codigo: "",
        nombre: "",
        cantidad: 1,
        precioUnitario: 0,
        descuento: 0,
        porcentajeIVA: 15,
        porcentajeICE: 0,
        aplicaIVA: true,
      };

      // Limpiar lista de productos en la factura
      this.productsInInvoice = [];

      // Reiniciar pie de página de la factura
      this.invoiceFooter = {
        descripcion: "",
        formaPago: "SIN UTILIZACION DEL SISTEMA FINANCIERO",
        valorPagar: 0,
        pagoPlazo: false,
        plazoDias: 0,
        formasPagoAgregadas: [],
      };

      // Limpiar búsqueda de productos
      this.productSearchQuery = "";
      this.productSuggestions = [];
      this.productSearchError = "";

      // Limpiar autocompletado de clientes
      this.sugerenciasClientes = [];
      this.mostrarSugerencias = false;
      this.indiceSugerenciaActiva = -1;

      // Limpiar modal nuevo cliente
      this.nuevoCliente = {
        identification_type_sri: '',
        identification_number: '',
        business_name: '',
        email: '',
        phone: '',
        address: ''
      };

      // Reiniciar estados de carga
      this.loadingSRI = false;
      this.loadingProductSearch = false;
      this.buscandoCliente = false;

      // Salir de modo edición
      this.facturaEditandoId = null;

      // Limpiar referencia a proforma origen
      this.proformaOrigen = null;
      // Limpiar referencia a pedido origen
      this.pedidoOrigen = null;
      // Limpiar referencia a orden de taller origen
      this.ordenTallerOrigen = null;

      // Reiniciar venta a crédito: no debe heredarse de la factura anterior
      this.invoiceHeader.esCreditoInterno = false;
      this.invoiceHeader.diasCredito = 30;
    },
    mostrarDocumentoFactura(sriData) {
      // Priorizar recibo térmico si está disponible
      if (sriData.recibo_url) {
        this.abrirVentanaRecibo(sriData.recibo_url);
      } else if (sriData.ride_url) {
        // Si no hay recibo, mostrar RIDE A4
        this.abrirVentanaRIDE(sriData.ride_url);
      } else {
        // Si no hay ninguno, solo notificar
        console.warn('No se generaron documentos RIDE ni recibo térmico');
      }
    },

    // -------------------------------------------------------------------------
    // Métodos del modal de cobro
    // -------------------------------------------------------------------------

    /**
     * Envía la factura al SRI para firma y autorización en segundo plano.
     * Retorna una Promise<boolean> que resuelve con true si fue autorizada.
     * No bloquea la UI — se lanza en paralelo al modal de cobro.
     */
    async enviarAlSriEnBackground(facturaId) {
      try {
        const { data } = await api.post(`/facturas/${facturaId}/firmar-enviar`);
        if (data?.success) {
          this.mostrarNotificacion(
            `SRI autorizó la factura. N.° ${data.numero_autorizacion || ''}`,
            'success'
          );
          return true;
        } else if (data?.en_proceso) {
          this.mostrarNotificacion('SRI procesando. Se consultará automáticamente.', 'info');
          this.consultarAutorizacionEnSegundoPlano(facturaId);
          return false;
        } else {
          if (await this.verificarAutorizacionActual(facturaId)) {
            return true;
          }
          const msg = data?.message || data?.error || 'El SRI no devolvió autorización';
          this.mostrarNotificacion(`SRI: ${msg}`, 'warning');
          return false;
        }
      } catch (err) {
        const statusErr = err.response?.status;
        const dataErr   = err.response?.data;
        if (statusErr === 202 || dataErr?.en_proceso) {
          this.mostrarNotificacion('SRI procesando. Se consultará automáticamente.', 'info');
          this.consultarAutorizacionEnSegundoPlano(facturaId);
        } else {
          if (await this.verificarAutorizacionActual(facturaId)) {
            return true;
          }
          const msg = dataErr?.message || dataErr?.error || err.message;
          this.mostrarNotificacion(`Factura guardada. SRI: ${msg}`, 'warning');
        }
        return false;
      }
    },

    /**
     * Carga los bancos activos para el selector del modal.
     */
    async cargarBancosDisponibles() {
      try {
        const { data } = await api.get('/bancos');
        this.bancos = (data?.bancos ?? data ?? []).filter(b => b.activo);
      } catch (e) {
        console.warn('[ClientVentas] No se pudieron cargar bancos:', e);
      }
    },

    /**
     * Abre el modal de cobro y espera a que el usuario confirme o lo omita.
     * Después de registrar el cobro (o saltar), imprime el ticket.
     */
    async abrirModalCobroPostFactura(facturaId, promesaFirma) {
      // Si viene de proforma con anticipo, cobrar solo el saldo pendiente
      const totalFactura = this.valorTotal ?? 0
      const po = this.proformaOrigen
      const totalACobrar = (po && po.total_anticipos > 0)
        ? po.saldo_pendiente
        : Number(parseFloat(totalFactura).toFixed(2))

      // Destino por defecto: la caja propia del usuario (nunca la de otro)
      const primeraCaja  = this.cajaActiva?.caja_id ?? '';

      this.modalCobro = {
        visible:          true,
        facturaId,
        autorizada:       false,     // se actualiza al confirmar cobro
        promesaFirma,               // Promise del SRI (ya corriendo en paralelo)
        total:            totalACobrar,
        recibido:         totalACobrar,
        totalFactura:     Number(parseFloat(totalFactura).toFixed(2)),
        anticipoProforma: po && po.total_anticipos > 0 ? po.total_anticipos : 0,
        numeroProforma:   po ? po.numero_proforma : null,
        metodo:           'efectivo',
        destinoId:        primeraCaja,
        numTransferencia: '',
        error:            '',
        guardando:        false,
        resolve:          null,
      };

      // Esperar a que el usuario confirme o salte (Promise resuelta en confirmarCobro / omitirCobro)
      return new Promise(resolve => { this.modalCobro.resolve = resolve; });
    },

    /**
     * Factura a crédito: omite cobro inmediato y pasa directo a ticket.
     */
    async imprimirTicketDirectoPostFactura(facturaId, promesaFirma) {
      let autorizada = false;
      try {
        const limite = new Promise(r => setTimeout(() => r(false), 20000));
        autorizada = await Promise.race([promesaFirma ?? Promise.resolve(false), limite]);
      } catch (_) {
        autorizada = false;
      }

      // Si viene de proforma, marcarla como convertida
      if (this.proformaOrigen && this.proformaOrigen.proforma_id) {
        try {
          await api.post(
            '/proformas/' + this.proformaOrigen.proforma_id + '/confirmar-conversion',
            { factura_id: facturaId }
          )
        } catch (_e) { /* no bloquear el flujo */ }
        this.proformaOrigen = null
      }

      // Si viene de pedido, marcarlo como facturado
      if (this.pedidoOrigen && this.pedidoOrigen.pedido_id) {
        try {
          await api.post(
            '/pedidos/' + this.pedidoOrigen.pedido_id + '/confirmar-conversion',
            { factura_id: facturaId }
          )
        } catch (_e) { /* no bloquear el flujo */ }
        this.pedidoOrigen = null
      }

      // Si viene de una orden de taller, marcarla como facturada
      if (this.ordenTallerOrigen && this.ordenTallerOrigen.orden_taller_id) {
        try {
          await api.post(
            '/ordenes-taller/' + this.ordenTallerOrigen.orden_taller_id + '/confirmar-conversion',
            { factura_id: facturaId }
          )
        } catch (_e) { /* no bloquear el flujo */ }
        this.ordenTallerOrigen = null
      }

      await this.imprimirTicketFactura(facturaId, autorizada);
    },

    /**
     * Valida y registra el cobro, luego imprime el ticket.
     */
    async confirmarCobro() {
      const mc = this.modalCobro;
      mc.error = '';

      if (!mc.destinoId) {
        mc.error = mc.metodo === 'efectivo'
          ? 'Selecciona una caja de destino.'
          : 'Selecciona un banco de destino.';
        return;
      }
      if (mc.metodo === 'efectivo' && (!mc.recibido || mc.recibido <= 0)) {
        mc.error = 'Ingresa el monto recibido.';
        return;
      }
      if (mc.metodo === 'efectivo' && mc.recibido < mc.total) {
        mc.error = `El monto recibido ($${parseFloat(mc.recibido).toFixed(2)}) es menor al total ($${parseFloat(mc.total).toFixed(2)}).`;
        return;
      }

      mc.guardando = true;
      try {
        const payload = {
          metodo_pago:     mc.metodo === 'efectivo' ? 'efectivo' : 'transferencia',
          destino_tipo:    mc.metodo === 'efectivo' ? 'caja'    : 'banco',
          destino_id:      mc.destinoId,
          monto:           mc.total,
        };
        if (mc.metodo === 'transferencia' && mc.numTransferencia) {
          payload.numero_comprobante = mc.numTransferencia;
        }
        await api.post(`/facturas/${mc.facturaId}/cobrar`, payload);
      } catch (e) {
        console.warn('[ClientVentas] Error al registrar cobro:', e);
        mc.error = e?.response?.data?.error || 'No se pudo registrar el cobro. El saldo de caja o banco no fue actualizado.';
        mc.guardando = false;
        return;
      }

        // Calcular vuelto para pasar al ticket
        const recibidoFinal = mc.metodo === 'efectivo'
          ? parseFloat(mc.recibido) || 0
          : mc.total;  // transferencia exacta
        const cambioFinal = parseFloat((recibidoFinal - mc.total).toFixed(2));

        // Esperar resultado del SRI — para este punto ya debería haber resuelto
        // (el usuario tardó al menos unos segundos llenando el cobro)
        let autorizada = false;
        try {
          const limite = new Promise(r => setTimeout(() => r(false), 20000));
          autorizada = await Promise.race([mc.promesaFirma ?? Promise.resolve(false), limite]);
        } catch (_) {
          autorizada = false;
        }

        mc.guardando = false;
        mc.visible   = false;

        // Si la factura viene de una proforma, marcarla como convertida
        if (this.proformaOrigen && this.proformaOrigen.proforma_id) {
          try {
            await api.post(
              '/proformas/' + this.proformaOrigen.proforma_id + '/confirmar-conversion',
              { factura_id: mc.facturaId }
            )
          } catch (_e) { /* no bloquear el flujo */ }
          this.proformaOrigen = null
        }

        // Si la factura viene de un pedido, marcarlo como facturado
        if (this.pedidoOrigen && this.pedidoOrigen.pedido_id) {
          try {
            await api.post(
              '/pedidos/' + this.pedidoOrigen.pedido_id + '/confirmar-conversion',
              { factura_id: mc.facturaId }
            )
          } catch (_e) { /* no bloquear el flujo */ }
          this.pedidoOrigen = null
        }

        // Si la factura viene de una orden de taller, marcarla como facturada
        if (this.ordenTallerOrigen && this.ordenTallerOrigen.orden_taller_id) {
          try {
            await api.post(
              '/ordenes-taller/' + this.ordenTallerOrigen.orden_taller_id + '/confirmar-conversion',
              { factura_id: mc.facturaId }
            )
          } catch (_e) { /* no bloquear el flujo */ }
          this.ordenTallerOrigen = null
        }

        // Imprimir ticket con datos de cobro embebidos
        await this.imprimirTicketFactura(mc.facturaId, autorizada, {
          recibido: recibidoFinal,
          cambio:   cambioFinal >= 0 ? cambioFinal : 0,
          metodo:   mc.metodo,
        });
        if (mc.resolve) mc.resolve();
    },

    /**
     * Salta el cobro y solo imprime el ticket.
     */
    async omitirCobro() {
      const mc = this.modalCobro;
      mc.visible = false;
      await this.imprimirTicketFactura(mc.facturaId, mc.autorizada);
      if (mc.resolve) mc.resolve();
    },

    // -------------------------------------------------------------------------

    /**
     * Obtiene el ticket térmico desde el backend y lo muestra/imprime.
     * Si la factura no está autorizada, agrega un aviso de no validez tributaria.
     * @param {object|null} pagoInfo - { recibido, cambio, metodo } para incrustar en el ticket
     */
    async imprimirTicketFactura(facturaId, autorizada, pagoInfo = null) {
      try {
        // Construir query string con datos de cobro si existen
        let endpoint = `/facturas/${facturaId}/ticket`;
        if (pagoInfo) {
          const params = {
            recibido: pagoInfo.recibido.toFixed(2),
            cambio:   pagoInfo.cambio.toFixed(2),
            metodo:   pagoInfo.metodo,
          }
          // Si viene de proforma con anticipo, enviar datos al ticket
          const po = this.proformaOrigen
          if (po && po.total_anticipos > 0) {
            params.anticipo         = po.total_anticipos.toFixed(2)
            params.numero_proforma  = po.numero_proforma || ''
          }
          endpoint += `?${new URLSearchParams(params).toString()}`;
        }
        const { data } = await api.get(endpoint);
        if (!data?.html) {
          console.warn('[ClientVentas] El ticket no retornó HTML');
          return;
        }

        let html = data.html;

        // Leer ancho de papel desde el meta tag generado por el backend
        const metaAncho = html.match(/<meta\s+name="paper-width"\s+content="(\d+)mm"/i);
        const anchoMm   = metaAncho ? parseInt(metaAncho[1], 10) : 80;

        const blob = new Blob([html], { type: 'text/html' });
        const url  = URL.createObjectURL(blob);

        if (this.esAndroid) {
          this._abrirTicketAndroid(html);
        } else {
          this.modalTicket.html    = url;
          this.modalTicket.anchoMm = anchoMm;
          this.modalTicket.visible = true;
        }
      } catch (e) {
        console.warn('[ClientVentas] No se pudo generar el ticket:', e.message);
      }
    },

    /**
     * Android: inyecta script de auto-impresión y abre en nueva ventana.
     * Chrome Android en ventana propia puede usar el servicio de impresión
     * del sistema operativo (Sunmi InnerPrinter, iMin, etc.).
     */
    _abrirTicketAndroid(html) {
      const scriptAutoPrint = `<script>window.addEventListener('load',function(){setTimeout(function(){window.print();},600);});</` + `script>`;
      const htmlFinal = html.includes('</body>')
        ? html.replace('</body>', scriptAutoPrint + '</body>')
        : html + scriptAutoPrint;
      const blob = new Blob([htmlFinal], { type: 'text/html' });
      const url  = URL.createObjectURL(blob);
      const win  = window.open(url, '_blank');
      if (!win || win.closed || typeof win.closed === 'undefined') {
        this.modalTicket.html    = URL.createObjectURL(new Blob([html], { type: 'text/html' }));
        this.modalTicket.visible = true;
      }
    },

    imprimirDesdeModal() {
      if (this.esAndroid && this.modalTicket.html) {
        const win = window.open(this.modalTicket.html, '_blank');
        if (win) { this.modalTicket.visible = false; return; }
      }

      const iframe  = this.$refs.iframeTicket;
      const anchoMm = this.modalTicket.anchoMm || 80;
      if (!iframe || !iframe.contentWindow) return;

      try {
        const doc = iframe.contentDocument || iframe.contentWindow.document;
        if (doc) {
          let estilo = doc.getElementById('__page_override');
          if (!estilo) {
            estilo = doc.createElement('style');
            estilo.id = '__page_override';
            doc.head.appendChild(estilo);
          }
          estilo.textContent = `
            @page { size: ${anchoMm}mm auto !important; margin: 2mm 1mm !important; }
            @media print { html, body { width: ${anchoMm}mm !important; margin: 0 !important; } }`;
        }
      } catch (e) { /* iframe origen cruzado */ }

      iframe.contentWindow.focus();
      iframe.contentWindow.print();
    },

    descargarTicketAndroid() {
      const url = this.modalTicket.html;
      if (!url) return;
      const a = document.createElement('a');
      a.href = url;
      a.download = 'ticket.html';
      a.click();
    },

    /**
     * Polling en segundo plano para facturas que quedaron EN PROCESAMIENTO en el SRI.
     * Se llama sin await para no bloquear el formulario y notifica al usuario al terminar.
     */
    async consultarAutorizacionEnSegundoPlano(facturaId, maxIntentos = 5, esperaMs = 12000) {
      for (let intento = 1; intento <= maxIntentos; intento++) {
        await new Promise(resolve => setTimeout(resolve, esperaMs));
        try {
          const { data } = await api.post(`/facturas/${facturaId}/consultar-autorizacion`);
          if (data?.success) {
            this.mostrarNotificacion(
              `Factura autorizada por el SRI. Autorización: ${data.numero_autorizacion || 'N/A'}`,
              'success'
            );
            return;
          }
          if (!data?.en_proceso) {
            if (await this.verificarAutorizacionActual(facturaId)) {
              return;
            }
            this.mostrarNotificacion(
              'El SRI no autorizó el comprobante: ' + (data?.message || 'Error desconocido'),
              'warning'
            );
            return;
          }
        } catch (err) {
          const status = err.response?.status;
          const errData = err.response?.data;
          if (status === 422) {
            if (await this.verificarAutorizacionActual(facturaId)) {
              return;
            }
            // Factura marcada RECHAZADA por el SRI
            this.mostrarNotificacion(
              'El SRI rechazó el comprobante: ' + (errData?.message || 'Error desconocido'),
              'warning'
            );
            return;
          }
          if (status !== 202) {
            if (await this.verificarAutorizacionActual(facturaId)) {
              return;
            }
            this.mostrarNotificacion(
              'Error al consultar autorización: ' + (errData?.message || err.message),
              'warning'
            );
            return;
          }
          // 202 = sigue en procesamiento, continuar polling
        }
      }
      this.mostrarNotificacion(
        'Se agotó el tiempo de espera. Revise el estado en Documentos Emitidos.',
        'warning'
      );
    },
    async verificarAutorizacionActual(facturaId) {
      try {
        const { data } = await api.get(`/facturas/${facturaId}`);
        const f = data?.data ?? data ?? null;
        const estado = Number(f?.estado ?? 0);
        const numeroAutorizacion = (f?.numero_autorizacion ?? '').toString().trim();
        const autorizada = estado === 5 || numeroAutorizacion.length > 0;

        if (autorizada) {
          this.mostrarNotificacion(
            `Factura autorizada por el SRI. Autorización: ${numeroAutorizacion || 'N/A'}`,
            'success'
          );
          return true;
        }
      } catch (e) {
        console.warn('[ClientVentas] No se pudo verificar autorización actual:', e);
      }
      return false;
    },
    abrirVentanaRecibo(url) {
      this.modalTicket.html    = url;
      this.modalTicket.visible = true;
    },
    abrirVentanaRIDE(url) {
      const ventana = window.open('', '_blank', 'width=900,height=1000,scrollbars=yes');
      if (!ventana) {
        this.mostrarNotificacion('No se pudo abrir la ventana. Verifica los bloqueadores de pop-ups.', 'warning');
        return;
      }
      
      ventana.document.write(`
        <!DOCTYPE html>
        <html>
        <head>
          <title>RIDE - Factura Electrónica</title>
          <style>
            body { 
              margin: 0; 
              padding: 0; 
              overflow: hidden;
            }
            iframe {
              width: 100%;
              height: calc(100vh - 60px);
              border: none;
            }
            .button-container {
              position: fixed;
              bottom: 0;
              left: 0;
              right: 0;
              background: #f8f9fa;
              padding: 10px;
              text-align: center;
              border-top: 2px solid #dee2e6;
            }
            button {
              padding: 10px 20px;
              font-size: 14px;
              cursor: pointer;
              border: none;
              border-radius: 5px;
              margin: 0 5px;
            }
            .btn-print {
              background: #2196F3;
              color: white;
            }
            .btn-close {
              background: #f44336;
              color: white;
            }
          </style>
        </head>
        <body>
          <iframe src="${url}"></iframe>
          <div class="button-container">
            <button class="btn-print" onclick="window.print()">Imprimir</button>
            <button class="btn-close" onclick="window.close()">Cerrar</button>
          </div>
        </body>
        </html>
      `);
    },
    async fetchSriTypes() {
      this.loadingSriTypes = true;
      try {
        const { data } = await api.get('/tipos-identificacion-sri');
        // La API devuelve { codigo, tipo_de_identificacion }; el template usa { code, name }
        this.sriTypes = (data.data || data || []).map(t => ({
          code: t.code ?? t.codigo ?? '',
          name: t.name ?? t.tipo_de_identificacion ?? ''
        })).filter(t => t.code !== '');
      } catch (error) {
        console.error('Error al cargar tipos SRI:', error);
        // Cargar tipos por defecto si falla la API
        this.sriTypes = [
          { code: '04', name: 'RUC' },
          { code: '05', name: 'Cédula' },
          { code: '06', name: 'Pasaporte' },
          { code: '07', name: 'Consumidor Final' },
          { code: '08', name: 'Identificación del Exterior' }
        ];
      } finally {
        this.loadingSriTypes = false;
      }
    },
    mostrarNotificacion(mensaje, tipo = 'info') {
      if (tipo === 'success') notificationService.success(mensaje);
      else if (tipo === 'error') notificationService.error(mensaje);
      else if (tipo === 'warning') notificationService.warning(mensaje);
      else notificationService.info(mensaje);
    },
    async cargarCodigosSri() {
      try {
        const { data } = await api.get('/validation-rules/sri-payment-methods');
        if (data.success) {
          this.sriPaymentCodes = Object.fromEntries(
            Object.entries(data.data || {}).map(([code, description]) => [
              String(code).padStart(2, '0'),
              description
            ])
          );
          // Convertir a array para el select
          this.paymentMethods = Object.entries(this.sriPaymentCodes).map(([code, description]) => ({
            value: description,
            label: `${code} - ${description}`
          }));
        }
      } catch (error) {
        console.error('Error al cargar códigos SRI:', error);
        // Códigos por defecto
        this.sriPaymentCodes = {
          '01': 'SIN UTILIZACION DEL SISTEMA FINANCIERO',
          '15': 'COMPENSACIÓN DE DEUDAS',
          '16': 'TARJETA DE DÉBITO',
          '17': 'DINERO ELECTRÓNICO',
          '18': 'TARJETA PREPAGO',
          '19': 'TARJETA DE CRÉDITO',
          '20': 'OTROS CON UTILIZACIÓN DEL SISTEMA FINANCIERO',
          '21': 'ENDOSO DE TÍTULOS'
        };
        // Convertir a array para el select
        this.paymentMethods = Object.entries(this.sriPaymentCodes).map(([code, description]) => ({
          value: description,
          label: `${code} - ${description}`
        }));
      }
    },
    async validarFacturaAntesGuardar() {
      // VALIDACIONES UX ÚNICAMENTE - Backend valida reglas SRI, cálculos fiscales y totales
      const errores = [];

      // Validar caja abierta (null = verificado y no hay; undefined = no se pudo verificar)
      // Plan basico no usa caja, se omite esta validacion.
      if (this.cajaActiva === null && !this.isPlanBasico) {
        errores.push('Debe abrir una caja antes de emitir facturas. Vaya al módulo de Control de Caja.');
      }

      // Validar que haya productos
      if (this.productsInInvoice.length === 0) {
        errores.push('Debe agregar al menos un producto a la factura');
      }

      // Nota: El campo descripción es opcional (no requerido por el SRI)

      // Validar forma de pago obligatoria
      if (!this.invoiceFooter.formaPago) {
        errores.push('La forma de pago es obligatoria');
      }

      // Validar datos del comprador
      if (!this.buyer.identificacion || this.buyer.identificacion.trim() === '') {
        errores.push('La identificación del comprador es obligatoria');
      }

      if (!this.buyer.razonSocial || this.buyer.razonSocial.trim() === '') {
        errores.push('La razón social del comprador es obligatoria');
      }

      // Validar que el tipo de identificación coincida con la longitud del número
      // (04 = RUC → 13 dígitos, 05 = Cédula → 10 dígitos). El backend valida el
      // algoritmo completo del SRI al guardar/actualizar el cliente.
      const tipoCodigoValidacion = this.mapTipoIdentToCode(this.buyer.tipoIdentificacion);
      const identifValidacion = (this.buyer.identificacion || '').trim();
      if (tipoCodigoValidacion === '04' && identifValidacion.length !== 13) {
        errores.push('El RUC del comprador debe tener 13 dígitos.');
      }
      if (tipoCodigoValidacion === '05' && identifValidacion.length !== 10) {
        errores.push('La cédula del comprador debe tener 10 dígitos.');
      }

      // Validación SRI: Consumidor Final (tipo 07 / 9999999999999) no puede tener importe total >= $50
      const esConsumidorFinal =
        this.buyer.tipoIdentificacion === '07' ||
        (this.buyer.identificacion || '').trim() === '9999999999999';
      if (esConsumidorFinal && this.valorTotal >= 50) {
        errores.push(
          `La factura supera los $50,00 de importe total, por lo que no puede ser emitida a nombre del CONSUMIDOR FINAL. ` +
          `Importe total actual: $${this.valorTotal.toFixed(2)}. ` +
          `Registre los datos del comprador (cédula, RUC o pasaporte) para continuar.`
        );
      }

      return errores;
    },
    async validarBorradorAntesGuardar(draftName, draftDescription) {
      // VALIDACIONES UX ÚNICAMENTE - Backend valida reglas SRI cuando se emita
      const errores = [];

      // Validar nombre del borrador obligatorio
      if (!draftName || draftName.trim() === '') {
        errores.push('El nombre del borrador es obligatorio');
      }

      // Validar descripción del borrador obligatoria
      if (!draftDescription || draftDescription.trim() === '') {
        errores.push('La descripción del borrador es obligatoria');
      }

      // Validar que haya productos (mínimo para guardar borrador)
      if (this.productsInInvoice.length === 0) {
        errores.push('Debe agregar al menos un producto para guardar el borrador');
      }

      return errores;
    },









    prepareInvoiceData() {
      // Preparar los datos de la factura en el formato esperado por el backend
      return {
        invoiceHeader: this.invoiceHeader,
        buyer: this.buyer,
        products: this.productsInInvoice,
        invoiceFooter: this.invoiceFooter,
        paymentMethod: this.paymentMethod
      };
    },






















  },
  async mounted() {
    this.invoiceFooter.valorPagar = this.valorTotal;
    await this.loadAuth();
    this.cargarModoDescuento();
    await this.verificarCajaActiva();
    await this.loadEmissionPoints();
    await this.loadSucursales();
    await this.loadVendedores();
    await this.loadAlmacenes();
    await this.loadAmbienteSri();
    this.fetchSriTypes();
    await this.cargarListaBorradores();
    await this.cargarCodigosSri();
    // Cargar destinos de cobro (transferencia); efectivo usa la caja propia (cajaActiva)
    this.cargarBancosDisponibles();
    // Avisos del superadmin (banner descartable), no bloquea el formulario
    this.cargarNotificacionesActivas();

    // Si viene ?edit={id}, cargar la factura para edición
    const editId = this.$route?.query?.edit
    if (editId) {
      await this.cargarFacturaParaEditar(Number(editId))
    }

    // Si viene ?view={id}, cargar la factura en modo solo lectura
    // (misma pantalla que editar, pero sin poder modificar nada)
    const viewId = this.$route?.query?.view
    if (viewId) {
      this.soloLectura = true
      await this.cargarFacturaParaEditar(Number(viewId))
    }

    // Si viene datos de pedido para convertir (sessionStorage)
    const pedidoRaw = sessionStorage.getItem('pedido_prefill')
    if (pedidoRaw) {
      sessionStorage.removeItem('pedido_prefill')
      try {
        await this.cargarDesdePedido(JSON.parse(pedidoRaw))
      } catch (_e) { /* silencioso */ }
    }

    // Si viene datos de proforma para convertir (sessionStorage)
    const proformaRaw = sessionStorage.getItem('proforma_prefill')
    if (proformaRaw) {
      sessionStorage.removeItem('proforma_prefill')
      try {
        await this.cargarDesdeProforma(JSON.parse(proformaRaw))
      } catch (_e) { /* silencioso */ }
    }

    // Si viene datos de orden de taller para convertir (sessionStorage)
    const ordenTallerRaw = sessionStorage.getItem('orden_taller_prefill')
    if (ordenTallerRaw) {
      sessionStorage.removeItem('orden_taller_prefill')
      try {
        await this.cargarDesdeOrdenTaller(JSON.parse(ordenTallerRaw))
      } catch (_e) { /* silencioso */ }
    }

    // Recarga automática de sucursales, solo si la pestaña está visible
    // (evita polling continuo de pestañas olvidadas en segundo plano)
    this.sucursalesReloadInterval = setInterval(() => {
      if (document.visibilityState === 'visible') {
        this.reloadSucursales();
      }
    }, 300000); // 5 minutos

    // Listener para cerrar dropdown de borradores al hacer clic fuera
    document.addEventListener('click', this.cerrarDropdownBorradores);
  },
  beforeUnmount() {
    clearTimeout(this.productSearchTimeout);
    clearTimeout(this.debounceTimer);
    // Limpiar intervalos
    if (this.sucursalesReloadInterval) {
      clearInterval(this.sucursalesReloadInterval);
    }
    // Limpiar listener
    document.removeEventListener('click', this.cerrarDropdownBorradores);
  },
};
</script>

<style>
h6 {
  font-size: 0.95rem;
  letter-spacing: 0.5px;
}

.card-header.bg-primary {
  background-color: #0d6efd !important; /* Asegura el color primario de Bootstrap */
}

/* Pequeño ajuste para que los input de la tabla se vean bien */
.table input.form-control-sm {
  max-width: 80px; /* Ancho máximo para campos de cantidad/precio en la tabla */
  margin: auto;
}
</style>



